<template>
  <div class="potree-measurements-app">
    <!-- Pinned Left Sidebar -->
    <PotreeSidebar
      v-if="viewerInstance"
      :viewer="viewerInstance"
      :potree-viewer-component="$refs.potreeViewer"
      :active-measurements="reactiveMeasurements"
      :selected-measurement="selectedCylinder"
      :active-lasso-ids="activeLassoIds"
      :cylinder-mode="cylinderMode"
      :current-step="currentStepNumber"
      :corrosion-state="corrosionState"
      :corrosion-thresholds="corrosionThresholds"
      :corrosion-statistics="corrosionStatistics"
      :tag-id="tagId"
      @sidebar-toggle="handleSidebarToggle"
      @sidebar-resize="handleSidebarResize"
      @toggle-lasso="toggleLassoActive"
      @delete-measurement="removeSingleMeasurement"
      @start-circle-1="startCircle1"
      @start-circle-2="startCircle2"
      @cancel-cylinder-mode="cancelCylinderMode"
      @finalize-cylinder="finalizeCylinder"
      @export-corrosion-report="handleExportCorrosionReport"
      @update-corrosion-thresholds="handleThresholdsChanged"
      @create-inspection-task="handleCreateInspect"
      @update-inspection-task="handleUpdateInspect"
      @cylinder-view-navigated="handleCylinderViewNavigated"
      @corrosion-enabled="handleCorrosionEnabled"
      @corrosion-disabled="handleCorrosionDisabled"
      @corrosion-error="handleCorrosionError"
      @select-measurement="handleMeasurementClick"
      @visibility-changed="handleVisibilityChanged"
    />

    <!-- Main Viewer -->
    <div class="potree-main">
      <PotreeViewer
        ref="potreeViewer"
        :point-cloud-url="pointCloudUrl"
        :point-cloud-name="pointCloudName"
        :point-budget="pointBudget"
        :fov="fov"
        :background="background"
        :language="language"
        :show-native-tools="false"
        @pointcloud-loaded="onPointCloudLoaded"
        @pointcloud-error="onPointCloudError"
        @viewer-ready="onViewerReady"
        @pointcloud-ready="onPointCloudReady"
        @potree-error="onPotreeError"
        @corrosion-stats-updated="handleCorrosionStatsUpdated" />

      <!-- Lasso Selection SVG Overlay -->
      <svg
        v-if="lassoTool.active"
        class="lasso-overlay"
        :class="{ 'locked': lassoTool.locked }"
        @click="handleLassoClick"
        @dblclick="handleLassoComplete"
        @mousemove="handleLassoHover"
        @contextmenu.prevent="cancelLasso"
      >
        <!-- Polygon being drawn -->
        <polygon
          v-if="lassoTool.points.length > 0"
          :points="getLassoPolygonPoints()"
          class="lasso-polygon"
        />

        <!-- Vertex markers -->
        <circle
          v-for="(point, index) in lassoPointsLocal"
          :key="index"
          :cx="point.x"
          :cy="point.y"
          r="5"
          class="lasso-vertex"
        />

        <!-- Preview line to cursor -->
        <line
          v-if="lassoPointsLocal.length > 0 && lassoHoverLocal"
          :x1="lassoPointsLocal[lassoPointsLocal.length - 1].x"
          :y1="lassoPointsLocal[lassoPointsLocal.length - 1].y"
          :x2="lassoHoverLocal.x"
          :y2="lassoHoverLocal.y"
          class="lasso-preview-line"
        />

        <!-- Instruction text -->
        <text x="20" y="40" class="lasso-instruction">
          {{ lassoTool.instruction }}
        </text>
      </svg>

      <!-- Right Side Vertical Measurement Toolbar (with integrated views) -->
      <v-sheet class="measurement-toolbar" elevation="8">
        <div class="toolbar-content">
          <!-- Views Section - Camera Toggle & Expandable Views -->
          <div class="views-section">
            <!-- Camera Mode Toggle Button (2D/3D) -->
            <v-tooltip left>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  @click="toggleCameraMode"
                  v-bind="attrs"
                  v-on="on"
                  icon
                  small
                  class="camera-toggle-btn"
                  :class="{
                    'perspective-mode': currentCameraMode === 'PERSPECTIVE',
                    'orthographic-mode': currentCameraMode === 'ORTHOGRAPHIC'
                  }"
                  :disabled="!viewerInstance">
                  <v-icon>{{ currentCameraMode === 'PERSPECTIVE' ? 'mdi-video-3d' : 'mdi-video-2d' }}</v-icon>
                </v-btn>
              </template>
              <span>Toggle Camera Mode (C)<br>Current: {{ currentCameraMode }}</span>
            </v-tooltip>

            <!-- Views Flyout Container -->
            <div
              class="views-flyout-container"
              @mouseenter="openViewsFlyout"
              @mouseleave="closeViewsFlyout">
              <!-- Views Trigger Button -->
              <v-tooltip left :disabled="viewsFlyoutOpen">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    icon
                    small
                    class="views-trigger-btn"
                    :class="{ 'flyout-open': viewsFlyoutOpen }"
                    :disabled="!viewerInstance">
                    <v-icon>mdi-axis-arrow</v-icon>
                  </v-btn>
                </template>
                <span>View Navigation</span>
              </v-tooltip>

              <!-- Interactive 3D ViewCube Flyout -->
              <transition name="flyout-slide">
                <div v-show="viewsFlyoutOpen" class="viewcube-flyout">
                  <!-- ViewCube Container -->
                  <div class="viewcube-wrapper">
                    <!-- 3D Scene Container - Draggable -->
                    <div
                      class="viewcube-scene"
                      :class="{ 'is-dragging': isDraggingCube }"
                      @mousedown="startCubeDrag"
                      @mousemove="onCubeDrag"
                      @mouseup="endCubeDrag"
                      @mouseleave="endCubeDrag">
                      <!-- Interactive 3D Cube -->
                      <div
                        class="viewcube"
                        :class="[
                          activeView ? `showing-${activeView.toLowerCase()}` : '',
                          { 'dragging': isDraggingCube }
                        ]"
                        :style="{ transform: viewCubeTransform }">

                        <!-- Front Face -->
                        <div
                          class="cube-face face-front"
                          :class="{ active: activeView === 'Front', hovered: hoveredFace === 'Front' }"
                          @click.stop="handleFaceClick('Front')"
                          @mouseenter="hoveredFace = 'Front'"
                          @mouseleave="hoveredFace = null">
                          <span class="face-label">FRONT</span>
                          <span class="face-key">F</span>
                        </div>

                        <!-- Back Face -->
                        <div
                          class="cube-face face-back"
                          :class="{ active: activeView === 'Back', hovered: hoveredFace === 'Back' }"
                          @click.stop="handleFaceClick('Back')"
                          @mouseenter="hoveredFace = 'Back'"
                          @mouseleave="hoveredFace = null">
                          <span class="face-label">BACK</span>
                          <span class="face-key">B</span>
                        </div>

                        <!-- Left Face -->
                        <div
                          class="cube-face face-left"
                          :class="{ active: activeView === 'Left', hovered: hoveredFace === 'Left' }"
                          @click.stop="handleFaceClick('Left')"
                          @mouseenter="hoveredFace = 'Left'"
                          @mouseleave="hoveredFace = null">
                          <span class="face-label">LEFT</span>
                          <span class="face-key">L</span>
                        </div>

                        <!-- Right Face -->
                        <div
                          class="cube-face face-right"
                          :class="{ active: activeView === 'Right', hovered: hoveredFace === 'Right' }"
                          @click.stop="handleFaceClick('Right')"
                          @mouseenter="hoveredFace = 'Right'"
                          @mouseleave="hoveredFace = null">
                          <span class="face-label">RIGHT</span>
                          <span class="face-key">R</span>
                        </div>

                        <!-- Top Face -->
                        <div
                          class="cube-face face-top"
                          :class="{ active: activeView === 'Top', hovered: hoveredFace === 'Top' }"
                          @click.stop="handleFaceClick('Top')"
                          @mouseenter="hoveredFace = 'Top'"
                          @mouseleave="hoveredFace = null">
                          <span class="face-label">TOP</span>
                          <span class="face-key">T</span>
                        </div>

                        <!-- Bottom Face -->
                        <div
                          class="cube-face face-bottom"
                          :class="{ active: activeView === 'Bottom', hovered: hoveredFace === 'Bottom' }"
                          @click.stop="handleFaceClick('Bottom')"
                          @mouseenter="hoveredFace = 'Bottom'"
                          @mouseleave="hoveredFace = null">
                          <span class="face-label">BOTTOM</span>
                          <span class="face-key">K</span>
                        </div>
                      </div>

                      <!-- Drag hint indicator -->
                      <div class="drag-hint" v-show="!isDraggingCube && !activeView">
                        <v-icon x-small color="rgba(255,255,255,0.4)">mdi-rotate-3d-variant</v-icon>
                      </div>
                    </div>

                    <!-- Compass Ring -->
                    <div class="compass-ring">
                      <span class="compass-n">N</span>
                      <span class="compass-e">E</span>
                      <span class="compass-s">S</span>
                      <span class="compass-w">W</span>
                    </div>
                  </div>

                  <!-- Status Bar -->
                  <div class="viewcube-status">
                    <div class="status-left">
                      <span class="status-dot" :class="{ active: activeView, dragging: isDraggingCube }"></span>
                      <span class="status-text">{{ isDraggingCube ? 'Rotating...' : (activeView || 'Drag to rotate') }}</span>
                    </div>
                    <div class="status-right">
                      <kbd class="reset-hint" @click="resetCubeView" title="Reset to isometric">
                        <v-icon x-small>mdi-restore</v-icon>
                      </kbd>
                    </div>
                  </div>
                </div>
              </transition>
            </div>
          </div>

          <!-- Divider between views and tools -->
          <v-divider class="toolbar-divider"></v-divider>

          <!-- Tools Section -->
          <div class="measurement-tools">
            <v-tooltip left v-for="tool in measurementTools" :key="tool.value">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  @click="setMeasurementTool(tool.value)"
                  v-bind="attrs"
                  v-on="on"
                  icon
                  small
                  :disabled="!isToolbarReady || isToolDisabledInCylinderMode(tool.value) || (tool.value === 'lasso' && activeLassoIds.length >= 8)"
                  class="measurement-btn"
                  :class="{ 'disabled-in-cylinder-mode': isToolDisabledInCylinderMode(tool.value) }">
                  <v-icon v-if="tool.useFallback || !tool.icon">{{ tool.fallbackIcon }}</v-icon>
                  <img
                    v-else
                    :src="tool.icon"
                    :alt="tool.text"
                    style="width: 20px; height: 20px; filter: sepia(0.5)"
                    @error="handleIconError(tool)" />
                </v-btn>
              </template>
              <span>
                {{ tool.text }}
                {{ !isToolbarReady ? ' (Loading...)' : '' }}
                {{ isToolDisabledInCylinderMode(tool.value) ? ' (Disabled in Cylinder Mode)' : '' }}
                {{ tool.value === 'lasso' && activeLassoIds.length >= 8 ? ' (Maximum 8 lassos reached)' : '' }}
                <span v-if="tool.value === 'lasso' && activeLassoIds.length > 0 && activeLassoIds.length < 8">
                  ({{ activeLassoIds.length }} active)
                </span>
              </span>
            </v-tooltip>

            <!-- Clean Lasso Button (eraser mode) -->
            <v-tooltip left>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  @click="startCleanLasso"
                  v-bind="attrs"
                  v-on="on"
                  icon
                  small
                  :disabled="!isToolbarReady || cleanLassoCount >= 8"
                  class="measurement-btn clean-lasso-btn"
                  :color="isCleanLassoMode ? 'error' : ''">
                  <v-icon>mdi-eraser</v-icon>
                </v-btn>
              </template>
              <span>
                Clean Lasso - Hide points inside selection
                {{ cleanLassoCount > 0 ? ` (${cleanLassoCount} active)` : '' }}
                {{ cleanLassoCount >= 8 ? ' (Maximum reached)' : '' }}
              </span>
            </v-tooltip>
          </div>

          <!-- Divider -->
          <v-divider class="toolbar-divider"></v-divider>

          <!-- Actions Section -->
          <div class="measurement-actions">
            <!-- Labels visibility toggle -->
            <v-tooltip left>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  icon
                  small
                  class="measurement-action-btn"
                  @click="toggleLabelsVisibility"
                  :disabled="!isToolbarReady">
                  <v-icon :color="labelsVisible ? 'primary' : ''">{{
                    labelsVisible ? 'mdi-label' : 'mdi-label-outline'
                  }}</v-icon>
                </v-btn>
              </template>
              <span>{{ labelsVisible ? 'Hide Labels' : 'Show Labels' }}</span>
            </v-tooltip>

            <!-- Measurement Count Badge -->
            <v-tooltip left v-if="activeMeasurements.length > 0">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  @click="toggleMeasurementsPanel"
                  v-bind="attrs"
                  v-on="on"
                  icon
                  small
                  class="measurement-count-btn">
                  <v-badge
                    :content="activeMeasurements.length"
                    color="primary"
                    overlap
                    offset-x="8"
                    offset-y="8">
                    <v-icon>mdi-ruler</v-icon>
                  </v-badge>
                </v-btn>
              </template>
              <span>{{ activeMeasurements.length }} Measurements</span>
            </v-tooltip>
          </div>

          <!-- AVA Inspect Section (Only when corrosion is enabled) -->
          <template v-if="corrosionState.enabled">
            <v-divider class="toolbar-divider"></v-divider>
            <div class="ava-inspect-section">
              <v-tooltip left>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    icon
                    small
                    class="ava-inspect-btn"
                    :class="{ 'update-mode': !!tagId }"
                    :disabled="!corrosionState.statistics"
                    @click="handleAvaInspectAction">
                    <v-icon>{{ tagId ? 'mdi-tag-check' : 'mdi-tag-plus' }}</v-icon>
                  </v-btn>
                </template>
                <span>
                  {{ tagId ? 'Update AVA Inspect' : 'Create AVA Inspect' }}
                  {{ !corrosionState.statistics ? ' (Run analysis first)' : '' }}
                </span>
              </v-tooltip>
            </div>
          </template>
        </div>
      </v-sheet>
    </div>

    <!-- Point Cloud Info Dialog -->
    <v-dialog v-model="showInfoDialog" max-width="500">
      <v-card>
        <v-card-title>
          <v-icon class="mr-2">mdi-information</v-icon>
          Point Cloud Information
        </v-card-title>
        <v-card-text v-if="pointCloudInfo">
          <v-simple-table dense>
            <tbody>
              <tr>
                <td><strong>Name:</strong></td>
                <td>{{ pointCloudInfo.name || 'Unknown' }}</td>
              </tr>
              <tr>
                <td><strong>Points:</strong></td>
                <td>
                  {{ pointCloudInfo.points ? pointCloudInfo.points.toLocaleString() : 'Unknown' }}
                </td>
              </tr>
              <tr>
                <td><strong>Bounds:</strong></td>
                <td>{{ formatBounds(pointCloudInfo) }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="showInfoDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
  /* eslint-disable no-unused-vars */
  const THREE = window.THREE;

  import PotreeViewer from './PotreeViewer.vue';
  import PotreeSidebar from './sidebar/PotreeSidebar.vue';
  // import ClippingToolbar from './navigation/ClippingToolbar.vue';
  import {
    logComponentCreated,
    logComponentMounted,
    logComponentDestroyed,
    logEvent,
    startTimer,
    log as debugLog,
    warn as debugWarn,
    error as debugError,
  } from '@/utils/debugLogger';
  import { getLayoutService } from '@/utils/layoutService';
  import { getDialogManager, DIALOG_TYPES } from '@/utils/dialogManager';
  import Z_INDEX from '@/utils/zIndexLayers';
  import HttpService from '@/utils/httpService';
  import { getBox3FromBoxVolume, getActiveCropBoxVolume } from '@/utils/pointCloudCoordinates';

  export default {
    name: 'PotreeMeasurementsApp',

    components: {
      PotreeViewer,
      PotreeSidebar,
      // ClippingToolbar,
      // CylinderAxisNavigation,
      // CorrosionAnalysisPanel,
      // CylinderModeControlPanel,
    },

    props: {
      // Point cloud data source
      pointCloudUrl: {
        type: String,
        // default: 'http://127.0.0.1:10000/devstoreaccount1/09fce7b2-e26c-485f-a30b-b238674e05fe/Image360/bbae4758-6491-4262-974d-c96c1193aa33/Potree/metadata.json'
        default: 'public/potree-measurements/pointclouds/test',
      },
      pointCloudName: {
        type: String,
        default: 'test',
      },

      // Viewer configuration
      pointBudget: {
        type: Number,
        default: Infinity,
      },
      fov: {
        type: Number,
        default: 60,
      },
      background: {
        type: String,
        default: 'black',
        validator: (value) => ['skybox', 'gradient', 'black', 'white', 'none'].includes(value),
      },
      language: {
        type: String,
        default: 'en',
      },

      // Corrosion analysis data from saved analysis result
      cylinderData: {
        type: Object,
        default: null,
      },
      corrosionThresholds: {
        type: Object,
        default: null,
      },
      corrosionStatistics: {
        type: Object,
        default: null,
      },
      // Tag ID for update functionality (if editing existing tag)
      tagId: {
        type: String,
        default: null,
      },
    },

    data() {
      return {
        pointCloudLoaded: false,
        pointCloudInfo: null,
        viewerInstance: null,
        pointCloudInstance: null,

        // Toolbar state
        activeMeasurementTool: null,
        currentPointBudget: Infinity,
        currentFOV: 60,
        currentBackground: 'black',
        selectedBackground: 'black',

        // Measurement state
        activeMeasurements: [],
        currentMeasurement: null,

        // Dialog states
        showMaterialDialog: false,
        showFiltersDialog: false,
        showInfoDialog: false,
        showMeasurementsPanel: true,

        // Labels visibility state
        labelsVisible: true,

        // Cylinder fitting mode state
        cylinderMode: {
          active: false,
          circles: [], // Keep for backward compatibility
          circle1Measurement: null, // Direct reference to Circle 1
          circle2Measurement: null, // Direct reference to Circle 2
          previewCylinder: null,
          keyboardListener: null,
          currentCirclePoints: [],
          currentStep: null, // 'circle1', 'circle2', or null
          circle1Complete: false, // Track Circle 1 completion status
          circle2Complete: false, // Track Circle 2 completion status
        },
        showCylinderInstructions: false,

        // Cylinder navigation state
        selectedCylinder: null,

        // Corrosion analysis state
        corrosionState: {
          enabled: false,
          statistics: null,
          cylinderData: null,
          clipVolumeActive: false,
          thresholds: null
        },

        // Corrosion panel drag state
        corrosionPanelPosition: { x: 0, y: 0 },
        corrosionPanelDragging: false,
        corrosionDragOffset: { x: 0, y: 0 },

        // Cylinder panel drag state
        cylinderPanelPosition: { x: 0, y: 0 },
        cylinderPanelDragging: false,
        cylinderDragOffset: { x: 0, y: 0 },

        // Panel state for draggable functionality
        panelPosition: { x: 0, y: 0 },
        panelDragging: false,
        dragOffset: { x: 0, y: 0 },

        // Service instances
        layoutService: null,
        dialogManager: null,

        // Dialog registrations
        corrosionPanelRegistration: null,

        // Tool definitions
        measurementTools: [
          // {
          //   value: 'point',
          //   text: 'Point',
          //   icon: '/potree-measurements/libs/potree/resources/icons/point.svg',
          //   fallbackIcon: 'mdi-map-marker',
          // },
          {
            value: 'distance',
            text: 'Distance',
            icon: '/potree-measurements/libs/potree/resources/icons/distance.svg',
            fallbackIcon: 'mdi-ruler',
          },
          // {
          //   value: 'area',
          //   text: 'Area',
          //   icon: '/potree-measurements/libs/potree/resources/icons/area.svg',
          //   fallbackIcon: 'mdi-vector-polygon',
          // },
          // {
          //   value: 'volume',
          //   text: 'Volume',
          //   icon: '/potree-measurements/libs/potree/resources/icons/volume.svg',
          //   fallbackIcon: 'mdi-cube-outline',
          // },
          // {
          //   value: 'angle',
          //   text: 'Angle',
          //   icon: '/potree-measurements/libs/potree/resources/icons/angle.png',
          //   fallbackIcon: 'mdi-angle-acute',
          // },
          // {
          //   value: 'height',
          //   text: 'Height',
          //   icon: '/potree-measurements/libs/potree/resources/icons/height.svg',
          //   fallbackIcon: 'mdi-arrow-expand-vertical',
          // },
          // {
          //   value: 'circle',
          //   text: 'Circle',
          //   icon: '/potree-measurements/libs/potree/resources/icons/circle.svg',
          //   fallbackIcon: 'mdi-circle-outline',
          // },
          {
            value: 'fit-cylinder',
            text: 'Fit Cylinder',
            icon: '/potree-measurements/libs/potree/resources/icons/cylinder.svg',
            fallbackIcon: 'mdi-cylinder',
          },
          {
            value: 'lasso',
            text: 'Lasso Selection',
            fallbackIcon: 'mdi-lasso',
            useFallback: true,
          },
          // {
          //   value: 'profile',
          //   text: 'Profile',
          //   icon: '/potree-measurements/libs/potree/resources/icons/profile.png',
          //   fallbackIcon: 'mdi-chart-line',
          // },
          // {
          //   value: 'annotation',
          //   text: 'Annotation',
          //   icon: '/potree-measurements/libs/potree/resources/icons/annotation.svg',
          //   fallbackIcon: 'mdi-comment-text',
          // },
        ],

        // Camera mode state (2D/3D toggle)
        currentCameraMode: 'PERSPECTIVE',

        // View navigation data with SVG icons
        viewNavigationItems: [
          { name: 'Top', label: 'Top (T)', icon: '/potree-measurements/libs/potree/resources/icons/top.svg' },
          { name: 'Bottom', label: 'Bottom (K)', icon: '/potree-measurements/libs/potree/resources/icons/bottom.svg' },
          { name: 'Front', label: 'Front (F)', icon: '/potree-measurements/libs/potree/resources/icons/front.svg' },
          { name: 'Back', label: 'Back (B)', icon: '/potree-measurements/libs/potree/resources/icons/back.svg' },
          { name: 'Left', label: 'Left (L)', icon: '/potree-measurements/libs/potree/resources/icons/left.svg' },
          { name: 'Right', label: 'Right (R)', icon: '/potree-measurements/libs/potree/resources/icons/right.svg' },
        ],

        // Views flyout hover state
        viewsFlyoutOpen: false,

        // Currently active view for visual feedback
        activeView: null,

        // ViewCube hover state
        hoveredFace: null,

        // ViewCube drag rotation state
        isDraggingCube: false,
        cubeDragStart: { x: 0, y: 0 },
        cubeRotation: { x: -25, y: -35 }, // Default isometric view
        cubeDragMoved: false, // Track if drag actually moved (to distinguish from click)

        // Keyboard handler for view navigation
        viewKeyboardHandler: null,

        cameraViews: [
          { value: 'left', text: 'Left View', icon: 'mdi-arrow-left' },
          { value: 'right', text: 'Right View', icon: 'mdi-arrow-right' },
          { value: 'front', text: 'Front View', icon: 'mdi-arrow-up' },
          { value: 'back', text: 'Back View', icon: 'mdi-arrow-down' },
          { value: 'top', text: 'Top View', icon: 'mdi-arrow-up-bold' },
          { value: 'bottom', text: 'Bottom View', icon: 'mdi-arrow-down-bold' },
        ],

        navigationControls: [
          {
            value: 'orbit',
            text: 'Orbit Controls',
            icon: '/potree-measurements/libs/potree/resources/icons/orbit_controls.svg',
          },
          {
            value: 'earth',
            text: 'Earth Controls',
            icon: '/potree-measurements/libs/potree/resources/icons/earth_controls.png',
          },
          {
            value: 'flight',
            text: 'Flight Controls',
            icon: '/potree-measurements/libs/potree/resources/icons/flight_controls.svg',
          },
          {
            value: 'helicopter',
            text: 'Helicopter Controls',
            icon: '/potree-measurements/libs/potree/resources/icons/helicopter_controls.svg',
          },
        ],

        backgroundOptions: [
          { text: 'Skybox', value: 'skybox' },
          { text: 'Gradient', value: 'gradient' },
          { text: 'Black', value: 'black' },
          { text: 'White', value: 'white' },
          { text: 'None', value: 'none' },
        ],

        // Screen-space lasso selection state
        lassoTool: {
          active: false,
          locked: false,
          points: [],          // [{x, y}] client coordinates for NDC conversion
          hoverPos: null,      // {x, y} current mouse position
          cameraState: null,   // Captured camera state
          instruction: 'Click to add points. Double-click, press Enter, or right-click to finish.',
        },

        lassoHistory: [],      // Array of completed lasso operations
        lassoHistoryIndex: -1, // Current position in history
        cameraLocked: false,   // Track if camera is currently locked
        lassoFilterInitialized: false,  // Track if buffer attributes are pre-created
        lassoDebugMeshes: {},  // Object of THREE.js meshes keyed by lasso ID
        lassoDebugEnabled: process.env.NODE_ENV === '!development',  // Toggle for debug visualization (enabled in dev only)

        // Multi-lasso support
        lassoCumulativeMode: 'intersect',  // 'union' | 'intersect' for combining multiple lassos
        activeLassoIds: [],            // IDs of currently active/applied lassos
        isCleanLassoMode: false,       // Whether next lasso will be a clean (exclusion) lasso

        // Colors for multi-lasso debug visualization (by type)
        lassoDebugColors: {
          filter: [0x00ff00, 0x0088ff, 0x00ffff, 0xff00ff],  // Green, blue, cyan, magenta
          clean: [0xff0000, 0xff8800, 0xff6666, 0xff88ff]    // Red, orange, coral, pink
        }
      };
    },

    computed: {
      isToolbarReady() {
        // For measurement tools, we only need the viewer to be ready
        return this.viewerInstance !== null;
      },

      // ViewCube transform based on active view or drag rotation
      viewCubeTransform() {
        // If dragging, use the drag rotation values
        if (this.isDraggingCube || !this.activeView) {
          return `rotateX(${this.cubeRotation.x}deg) rotateY(${this.cubeRotation.y}deg)`;
        }

        // Predefined view transforms
        const transforms = {
          Front: 'rotateX(0deg) rotateY(0deg)',
          Back: 'rotateX(0deg) rotateY(180deg)',
          Left: 'rotateX(0deg) rotateY(90deg)',
          Right: 'rotateX(0deg) rotateY(-90deg)',
          Top: 'rotateX(-90deg) rotateY(0deg)',
          Bottom: 'rotateX(90deg) rotateY(0deg)',
        };
        return transforms[this.activeView];
      },

      // Computed property to ensure measurements are reactive
      reactiveMeasurements() {
        return this.activeMeasurements.map((item) => ({
          ...item,
          name: this.generateMeasurementName(item),
          currentValue: this.getCompactMeasurementValue(item),
        }));
      },

      // Lasso type computed properties
      filterLassos() {
        return this.lassoHistory.filter(l => l.type === 'filter' || !l.type);
      },
      cleanLassos() {
        return this.lassoHistory.filter(l => l.type === 'clean');
      },
      filterLassoCount() {
        return this.filterLassos.length;
      },
      cleanLassoCount() {
        return this.cleanLassos.length;
      },

      // Cylinder mode computed properties
      circle1Status() {
        if (this.cylinderMode.circle1Complete) return 'complete';
        if (this.cylinderMode.currentStep === 'circle1') return 'active';
        return 'pending';
      },

      circle2Status() {
        if (this.cylinderMode.circle2Complete) return 'complete';
        if (this.cylinderMode.currentStep === 'circle2') return 'active';
        return 'pending';
      },

      circle1ButtonText() {
        if (this.circle1Status === 'complete') return 'Recreate Circle 1';
        if (this.circle1Status === 'active') return 'Placing Circle 1...';
        return 'Start Circle 1';
      },

      circle2ButtonText() {
        if (this.circle2Status === 'complete') return 'Recreate Circle 2';
        if (this.circle2Status === 'active') return 'Placing Circle 2...';
        return 'Start Circle 2';
      },

      currentStepNumber() {
        // Return actual step being placed, not progress
        // This ensures Circle 2 shows "Start" (not "Placing...") after Circle 1 completes
        if (this.cylinderMode.currentStep === 'circle1') return 1;
        if (this.cylinderMode.currentStep === 'circle2') return 2;
        return null; // Not actively placing
      },

      // Convert client coordinates to local SVG coordinates for display
      lassoPointsLocal() {
        if (!this.lassoTool.points.length) return [];

        // Get the SVG container's position
        const viewer = this.getViewer();
        if (!viewer || !viewer.renderer) return this.lassoTool.points;

        const canvas = viewer.renderer.domElement;
        const rect = canvas.getBoundingClientRect();

        return this.lassoTool.points.map(p => ({
          x: p.x - rect.left,
          y: p.y - rect.top
        }));
      },

      lassoHoverLocal() {
        if (!this.lassoTool.hoverPos) return null;

        const viewer = this.getViewer();
        if (!viewer || !viewer.renderer) return this.lassoTool.hoverPos;

        const canvas = viewer.renderer.domElement;
        const rect = canvas.getBoundingClientRect();

        return {
          x: this.lassoTool.hoverPos.x - rect.left,
          y: this.lassoTool.hoverPos.y - rect.top
        };
      },

    },

    watch: {
      pointBudget: {
        handler(newVal) {
          this.currentPointBudget = newVal;
        },
        immediate: true,
      },
      fov: {
        handler(newVal) {
          this.currentFOV = newVal;
        },
        immediate: true,
      },
      background: {
        handler(newVal) {
          this.currentBackground = newVal;
          this.selectedBackground = newVal;
        },
        immediate: true,
      },
      selectedBackground: {
        handler(newVal) {
          this.currentBackground = newVal;
        },
        immediate: true,
      },

      // Watch for Corrosion Panel visibility to register/unregister with DialogManager
      'corrosionState.enabled': {
        handler(isEnabled) {
          if (isEnabled) {
            // Register the corrosion panel with DialogManager
            if (this.dialogManager) {
              this.corrosionPanelRegistration = this.dialogManager.registerDialog({
                id: 'corrosion-panel',
                type: DIALOG_TYPES.PANEL,
                onClose: () => {
                  this.handleCorrosionDisabled();
                },
                baseZIndex: Z_INDEX.VIEWER_FLOATING,
                closeOnEscape: true,
                trapFocus: false
              });

              debugLog('[PotreeMeasurementsApp] Corrosion panel registered with DialogManager');
            }
          } else {
            // Unregister the corrosion panel
            if (this.corrosionPanelRegistration) {
              if (this.dialogManager && this.dialogManager.unregisterDialog) {
                this.dialogManager.unregisterDialog(this.corrosionPanelRegistration.id);
              }
              this.corrosionPanelRegistration = null;
              debugLog('[PotreeMeasurementsApp] Corrosion panel unregistered from DialogManager');
            }
          }
        }
      },
    },

    created() {
      logComponentCreated('PotreeMeasurementsApp', {
        pointCloudUrl: this.pointCloudUrl,
        pointCloudName: this.pointCloudName,
        pointBudget: this.pointBudget,
        fov: this.fov,
        background: this.background,
      });

      // Initialize layout service for sidebar-aware positioning
      this.layoutService = getLayoutService(this.$store);

      // Initialize dialog manager for focus and z-index management
      this.dialogManager = getDialogManager();

      // Expose debug logger globally for runtime configuration
      if (!window.potreeDebugLogger) {
        window.potreeDebugLogger = require('@/utils/debugLogger').default;
        debugLog(
          '%c🐛 Potree Debug Logger Available',
          'background: #4CAF50; color: white; padding: 8px 16px; font-weight: bold; border-radius: 4px;',
        );
        debugLog(
          '%cUse window.potreeDebugLogger to configure debug logging',
          'color: #666; font-style: italic;',
        );
        debugLog(
          '%cExample: window.potreeDebugLogger.enableDebug(false)',
          'color: #666; font-style: italic;',
        );
      }
    },

    mounted() {
      logComponentMounted('PotreeMeasurementsApp', {
        measurementTools: this.measurementTools.length,
        cameraViews: this.cameraViews.length,
        navigationControls: this.navigationControls.length,
      });

      // Load panel positions
      this.loadPanelPosition();
      this.loadCorrosionPanelPosition();
      this.loadCylinderPanelPosition();

      // Add event listeners for measurements panel drag
      document.addEventListener('mousemove', this.handlePanelDrag);
      document.addEventListener('mouseup', this.stopPanelDrag);

      // Add event listeners for corrosion panel drag
      document.addEventListener('mousemove', this.handleCorrosionPanelDrag);
      document.addEventListener('mouseup', this.stopCorrosionPanelDrag);

      // Add event listeners for cylinder panel drag
      document.addEventListener('mousemove', this.handleCylinderPanelDrag);
      document.addEventListener('mouseup', this.stopCylinderPanelDrag);

      // Setup view navigation keyboard shortcuts (F, B, L, R, T, K, C)
      this.setupViewKeyboardShortcuts();
    },

    beforeDestroy() {
      logComponentDestroyed('PotreeMeasurementsApp', {
        activeMeasurements: this.activeMeasurements.length,
        viewerInstance: !!this.viewerInstance,
        pointCloudLoaded: this.pointCloudLoaded,
      });

      // Reset lasso filter global state to prevent persistence across viewer sessions
      if (window.AVA_LASSO_DATA) {
        window.AVA_LASSO_DATA.enabled = false;
        window.AVA_LASSO_DATA.filterMode = 'inside';
        window.AVA_LASSO_DATA.polygon = [];
        window.AVA_LASSO_DATA.polygonNDC = [];
        window.AVA_LASSO_DATA.cameraState = null;
        window.AVA_LASSO_DATA.vertexCount = 0;
        window.AVA_LASSO_DATA.boundingBox = { minX: -1, minY: -1, maxX: 1, maxY: 1 };
        window.AVA_LASSO_DATA.lassoId = null;
        window.AVA_LASSO_DATA.timestamp = null;
      }

      // Clear lasso debug mesh
      this.clearLassoDebugMesh();

      // Reset local lasso state
      this.lassoHistory = [];
      this.lassoHistoryIndex = -1;

      // Clear measurements before destroying
      if (
        this.viewerInstance &&
        this.viewerInstance.scene &&
        this.viewerInstance.scene.measurements
      ) {
        const measurements = [...this.viewerInstance.scene.measurements];
        measurements.forEach((m) => {
          if (this.viewerInstance.scene.removeMeasurement) {
            this.viewerInstance.scene.removeMeasurement(m);
          }
        });
      }

      // Clear references
      this.viewerInstance = null;
      this.pointCloudInstance = null;
      this.activeMeasurements = [];

      // Save panel positions (measurements panel position not saved - always uses default in dialog)
      // this.savePanelPosition(); // Disabled - using default position on mount
      this.saveCorrosionPanelPosition();
      this.saveCylinderPanelPosition();

      // Remove event listeners for measurements panel drag
      document.removeEventListener('mousemove', this.handlePanelDrag);
      document.removeEventListener('mouseup', this.stopPanelDrag);

      // Remove event listeners for corrosion panel drag
      document.removeEventListener('mousemove', this.handleCorrosionPanelDrag);
      document.removeEventListener('mouseup', this.stopCorrosionPanelDrag);

      // Remove event listeners for cylinder panel drag
      document.removeEventListener('mousemove', this.handleCylinderPanelDrag);
      document.removeEventListener('mouseup', this.stopCylinderPanelDrag);

      // Remove view navigation keyboard shortcuts
      this.removeViewKeyboardShortcuts();
    },

    methods: {
      onViewerReady(viewer) {
        this.viewerInstance = viewer;

        logEvent('Viewer Ready', {
          viewer: 'PotreeViewer',
          scene: !!viewer.scene,
          camera: !!viewer.camera,
        });

        // Re-emit to parent components (e.g., CroppedMeasurementsDialog)
        this.$emit('viewer-ready', viewer);
      },

      onPointCloudReady({ viewer, pointcloud }) {
        this.viewerInstance = viewer;
        this.pointCloudInstance = pointcloud;
        this.pointCloudLoaded = true;
        this.pointCloudInfo = pointcloud;

        logEvent('Point Cloud Ready', {
          name: this.pointCloudName,
          loaded: true,
          hasViewer: !!viewer,
          hasPointCloud: !!pointcloud,
        });

        // Restore saved lasso selection from analysis result (if available)
        this.$nextTick(() => {
          this.restoreLassoFromAnalysisResult();
        });

        // If cylinderData is provided, create a virtual cylinder measurement
        if (this.cylinderData) {
          // Use $nextTick to ensure viewer and scene are fully initialized
          this.$nextTick(() => {
            try {
              // Create a virtual cylinder measurement object
              // Structure must match real cylinder measurements
              // cylinderData property is required for extractCylinderData() in cylinderCameraUtils.js
              const virtualCylinder = {
                id: 'analysis-cylinder',
                type: 'fitted-cylinder',
                name: 'Analysis Cylinder',
                cylinderData: {
                  position: this.cylinderData.position,
                  axis: this.cylinderData.axis,
                  radius: this.cylinderData.radius,
                  height: this.cylinderData.height,
                },
                // Mark as from analysis (not manually created)
                fromAnalysis: true,
              };

              // Auto-select this cylinder
              this.selectedCylinder = virtualCylinder;

              // Add to activeMeasurements to make panel visible
              this.activeMeasurements.push(virtualCylinder);
            } catch (error) {
              debugError('[PotreeMeasurementsApp] Error creating virtual cylinder:', error);
            }
          });
        }

        // Re-emit to parent components (e.g., CroppedMeasurementsDialog)
        this.$emit('pointcloud-ready', { viewer, pointcloud });
      },

      onPointCloudLoaded(pointcloud) {
        this.pointCloudLoaded = true;
        this.pointCloudInfo = pointcloud;
        this.pointCloudInstance = pointcloud;

        logEvent('Point Cloud Loaded Event', {
          name: this.pointCloudName,
          numPoints: pointcloud.numPoints || 'unknown',
        });

        // Emit event for external listeners
        this.$emit('pointcloud-loaded', pointcloud);
      },

      onPointCloudError(error) {
        this.pointCloudLoaded = false;
        this.pointCloudInfo = null;
        this.pointCloudInstance = null;

        logEvent('Point Cloud Error', {
          error: error.message || error,
          pointCloudUrl: this.pointCloudUrl,
        });

        // Emit error event for external listeners
        this.$emit('pointcloud-error', error);
      },

      onPotreeError(error) {
        debugError('Potree error:', error);

        logEvent('Potree Error', {
          error: error.message || error,
        });

        this.$emit('potree-error', error);
      },

      // Navigation Event Handlers
      handleViewChange(viewData) {
        debugLog('View changed via ViewCube:', viewData);
        this.$emit('view-changed', viewData);
      },

      handleCylinderViewNavigated(viewData) {
        debugLog('Cylinder view navigated:', viewData);
        this.$emit('cylinder-view-navigated', viewData);
      },

      // Corrosion Analysis Event Handlers
      handleCorrosionEnabled(payload) {
        // Handle payload with optional clipVolumeActive
        const { statistics, cylinderData, clipVolumeActive = false } = payload || {};

        debugLog('[App] Corrosion enabled:', {
          statistics,
          cylinderData,
          clipVolumeActive,
          hasSavedThresholds: !!this.corrosionThresholds
        });

        this.corrosionState.enabled = true;
        this.corrosionState.statistics = statistics;
        this.corrosionState.cylinderData = cylinderData;
        this.corrosionState.clipVolumeActive = clipVolumeActive;

        // Use saved thresholds if available (from loaded analysis), otherwise use defaults
        // Use provided thresholds or default to enums.js category boundaries
        const thresholds = this.corrosionThresholds || {
          normalToMinor: 0.004,      // 4mm - Cat A -> Cat B
          minorToModerate: 0.006,    // 6mm - Cat B -> Cat C
          moderateToCritical: 0.009, // 9mm - Cat C -> Cat D
          maxDisplay: 0.015          // 15mm
        };

        this.corrosionState.thresholds = thresholds;

        // Apply thresholds to point cloud visualization immediately
        // Use $nextTick to ensure PotreeViewer ref is available
        this.$nextTick(() => {
          if (this.$refs.potreeViewer && this.$refs.potreeViewer.updateCorrosionGradientWithThresholds) {
            debugLog('[App] Applying thresholds to visualization:', {
              thresholds,
              source: this.corrosionThresholds ? 'saved' : 'default'
            });
            this.$refs.potreeViewer.updateCorrosionGradientWithThresholds(thresholds);
          } else {
            debugWarn('[App] PotreeViewer or updateCorrosionGradientWithThresholds not available');
          }
        });
      },

      handleCorrosionDisabled() {
        debugLog('[App] Corrosion disabled');
        this.corrosionState.enabled = false;
        this.corrosionState.statistics = null;
        this.corrosionState.cylinderData = null;
        this.corrosionState.clipVolumeActive = false;
        this.corrosionState.thresholds = null;
      },

      handleCorrosionError(error) {
        debugError('[App] Corrosion error:', error);
      },

      handleCorrosionStatsUpdated(newStatistics, clipVolumeActive) {
        debugLog('[App] Corrosion statistics updated:', { newStatistics, clipVolumeActive });
        this.corrosionState.statistics = newStatistics;
        this.corrosionState.clipVolumeActive = clipVolumeActive;
      },

      handleThresholdsChanged(thresholds) {
        debugLog('[App] Thresholds changed:', thresholds);

        // Store thresholds in state for tag creation
        this.corrosionState.thresholds = thresholds;

        // Call PotreeViewer to update the gradient with custom thresholds
        if (this.$refs.potreeViewer && this.$refs.potreeViewer.updateCorrosionGradientWithThresholds) {
          this.$refs.potreeViewer.updateCorrosionGradientWithThresholds(thresholds);
        }
      },

      handlePresetApplied(presetName) {
        debugLog('[App] Preset applied:', presetName);
      },

      handleResetThresholds() {
        debugLog('[App] Reset thresholds to defaults');
      },

      handleVisibilityChanged(visibilitySettings) {
        debugLog('[App] Visibility changed:', visibilitySettings);

        // Call PotreeViewer to update visibility flags
        if (this.$refs.potreeViewer && this.$refs.potreeViewer.updateCorrosionVisibility) {
          this.$refs.potreeViewer.updateCorrosionVisibility(visibilitySettings);
        }
      },

      handleExportCorrosionReport() {
        debugLog('[App] Exporting corrosion report');

        try {
          if (this.$refs.potreeViewer && this.$refs.potreeViewer.exportCorrosionData) {
            const csv = this.$refs.potreeViewer.exportCorrosionData(this.corrosionState.cylinderData);

            // Download CSV
            const blob = new Blob([csv], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `corrosion-report-${Date.now()}.csv`;
            a.click();
            URL.revokeObjectURL(url);
          }
        } catch (error) {
          debugError('[App] Error exporting corrosion report:', error);
        }
      },

      /**
       * Upload heatmap snapshot to server
       * @param {string} base64Data - Base64 encoded PNG image
       * @param {string} identifier - Identifier for filename
       * @returns {Promise<string|null>} - Storage URL or null on failure
       */
      async uploadHeatmapSnapshot(base64Data, identifier) {
        if (!base64Data) return null;

        try {
          debugLog('[App] Uploading heatmap snapshot...');

          // Convert base64 to Blob
          const base64Content = base64Data.split(',')[1];
          const byteCharacters = atob(base64Content);
          const byteNumbers = [];

          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers.push(byteCharacters.charCodeAt(i));
          }

          const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'image/png' });
          const filename = `heatmap_${identifier}_${Date.now()}.png`;
          const file = new File([blob], filename, { type: 'image/png' });

          // Create FormData
          const formData = new FormData();
          formData.append('image', file);

          // Upload to AVA Inspect Image endpoint
          const response = await HttpService.post({
            url: 'api/tags/ava-inspect/Image',
            data: formData,
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });

          if (response.data && response.data.url) {
            debugLog('[App] ✅ Heatmap uploaded:', response.data.url);
            return response.data.url;
          }

          debugWarn('[App] ⚠️ No URL in response');
          return null;
        } catch (error) {
          debugError('[App] ❌ Upload failed:', error);
          return null;
        }
      },

      /**
       * Handle AVA Inspect action from toolbar button
       * Routes to create or update based on tagId
       */
      handleAvaInspectAction() {
        if (this.tagId) {
          this.handleUpdateInspect({ tagId: this.tagId });
        } else {
          this.handleCreateInspect();
        }
      },

      // ========================================
      // View Navigation Methods
      // ========================================

      /**
       * Toggle camera mode between PERSPECTIVE and ORTHOGRAPHIC
       */
      toggleCameraMode() {
        if (!this.viewerInstance) {
          debugWarn('[App] Viewer not available for camera mode toggle');
          return;
        }

        this.currentCameraMode = this.currentCameraMode === 'PERSPECTIVE' ? 'ORTHOGRAPHIC' : 'PERSPECTIVE';

        if (window.Potree && window.Potree.CameraMode) {
          const cameraMode = window.Potree.CameraMode[this.currentCameraMode];
          if (cameraMode !== undefined) {
            this.viewerInstance.setCameraMode(cameraMode);
            if (window.AVA_POTREE_VIEWER) {
              window.AVA_POTREE_VIEWER.useHQ = this.currentCameraMode !== 'ORTHOGRAPHIC';
            }
            debugLog('[App] Camera mode changed to:', this.currentCameraMode);
          }
        }
      },

      /**
       * Set view (Top, Bottom, Front, Back, Left, Right)
       */
      setView(viewName) {
        if (!this.viewerInstance) {
          debugWarn('[App] Viewer not available for view change');
          return;
        }

        // Track active view for visual feedback
        this.activeView = viewName;

        // Check for active clip volume
        const clipVolume = this.viewerInstance.scene?.volumes?.find(v => v.clip === true);

        if (clipVolume) {
          this.setClipVolumeView(viewName, clipVolume);
        } else {
          const methodName = `set${viewName}View`;
          if (typeof this.viewerInstance[methodName] === 'function') {
            this.viewerInstance[methodName]();
            debugLog('[App] View changed to:', viewName);
          }
        }
      },

      /**
       * Set view based on clip volume's bounding box
       */
      setClipVolumeView(viewName, clipVolume) {
        if (!clipVolume?.position || !clipVolume?.scale) return;

        const THREE = window.THREE;
        const center = new THREE.Vector3(
          clipVolume.position.x,
          clipVolume.position.y,
          clipVolume.position.z
        );

        const size = new THREE.Vector3(
          clipVolume.scale.x,
          clipVolume.scale.y,
          clipVolume.scale.z
        );

        const maxDimension = Math.max(size.x, size.y, size.z);
        const distance = maxDimension * 1.5;

        let cameraPosition = new THREE.Vector3();
        let upVector = new THREE.Vector3(0, 0, 1);

        switch (viewName) {
          case 'Top':
            cameraPosition.set(center.x, center.y, center.z + distance);
            upVector.set(0, 1, 0);
            break;
          case 'Bottom':
            cameraPosition.set(center.x, center.y, center.z - distance);
            upVector.set(0, 1, 0);
            break;
          case 'Front':
            cameraPosition.set(center.x, center.y + distance, center.z);
            upVector.set(0, 0, 1);
            break;
          case 'Back':
            cameraPosition.set(center.x, center.y - distance, center.z);
            upVector.set(0, 0, 1);
            break;
          case 'Left':
            cameraPosition.set(center.x - distance, center.y, center.z);
            upVector.set(0, 0, 1);
            break;
          case 'Right':
            cameraPosition.set(center.x + distance, center.y, center.z);
            upVector.set(0, 0, 1);
            break;
          default:
            return;
        }

        const duration = 500;
        if (this.viewerInstance.scene?.view) {
          this.viewerInstance.scene.view.setView(cameraPosition, center, duration, () => {
            if (this.viewerInstance.setNeedsRedraw) {
              this.viewerInstance.setNeedsRedraw();
            }
          });
        }
      },

      /**
       * Open views flyout
       */
      openViewsFlyout() {
        this.viewsFlyoutOpen = true;
      },

      /**
       * Close views flyout
       */
      closeViewsFlyout() {
        this.viewsFlyoutOpen = false;
      },

      /**
       * Reset ViewCube to default isometric view
       */
      resetCubeView() {
        this.activeView = null;
        this.hoveredFace = null;
        this.cubeRotation = { x: -25, y: -35 }; // Reset to default isometric
      },

      /**
       * Start dragging the ViewCube
       */
      startCubeDrag(event) {
        // Only start drag on left mouse button
        if (event.button !== 0) return;

        this.isDraggingCube = true;
        this.cubeDragMoved = false;
        this.cubeDragStart = {
          x: event.clientX,
          y: event.clientY
        };

        // Clear active view when starting to drag
        this.activeView = null;
      },

      /**
       * Handle cube drag movement
       */
      onCubeDrag(event) {
        if (!this.isDraggingCube) return;

        const deltaX = event.clientX - this.cubeDragStart.x;
        const deltaY = event.clientY - this.cubeDragStart.y;

        // Mark as moved if there's significant movement (to distinguish from click)
        if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
          this.cubeDragMoved = true;
        }

        // Sensitivity factor for rotation
        const sensitivity = 0.8;

        // Update rotation (Y axis for horizontal drag, X axis for vertical drag)
        this.cubeRotation = {
          x: this.cubeRotation.x - deltaY * sensitivity,
          y: this.cubeRotation.y + deltaX * sensitivity
        };

        // Clamp X rotation to prevent flipping
        this.cubeRotation.x = Math.max(-90, Math.min(90, this.cubeRotation.x));

        // Update start position for next frame
        this.cubeDragStart = {
          x: event.clientX,
          y: event.clientY
        };
      },

      /**
       * End cube drag
       */
      endCubeDrag() {
        this.isDraggingCube = false;
      },

      /**
       * Handle face click - only trigger if not dragging
       */
      handleFaceClick(viewName) {
        // Don't trigger view change if we were dragging
        if (this.cubeDragMoved) {
          this.cubeDragMoved = false;
          return;
        }

        this.setView(viewName);
      },

      /**
       * Setup keyboard shortcuts for view navigation
       * F=Front, B=Back, L=Left, R=Right, T=Top, K=Bottom, C=Camera Toggle
       */
      setupViewKeyboardShortcuts() {
        this.viewKeyboardHandler = (e) => {
          // Ignore when typing in input fields
          if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
            return;
          }

          if (!this.viewerInstance) return;

          const key = e.key.toLowerCase();
          const shortcuts = {
            'f': 'Front',
            'b': 'Back',
            'l': 'Left',
            'r': 'Right',
            't': 'Top',
            'k': 'Bottom',
            'c': 'cameraToggle'
          };

          const action = shortcuts[key];
          if (action) {
            e.preventDefault();
            if (action === 'cameraToggle') {
              this.toggleCameraMode();
            } else {
              this.setView(action);
            }
          }
        };

        document.addEventListener('keydown', this.viewKeyboardHandler);
      },

      /**
       * Remove view navigation keyboard shortcuts
       */
      removeViewKeyboardShortcuts() {
        if (this.viewKeyboardHandler) {
          document.removeEventListener('keydown', this.viewKeyboardHandler);
          this.viewKeyboardHandler = null;
        }
      },

      async handleCreateInspect() {
        debugLog('[App] Creating AVA Inspect tag from corrosion analysis');

        try {
          // Capture heatmap snapshot
          debugLog('[App] 📸 Capturing heatmap...');
          const snapshotBase64 = this.$refs.potreeViewer?.captureSnapshot();
          let heatMapUrl = null;

          if (snapshotBase64) {
            heatMapUrl = await this.uploadHeatmapSnapshot(snapshotBase64, 'create');

            if (!heatMapUrl) {
              debugWarn('[App] Failed to upload heatmap, continuing without it');
            }
          }

          // Get point cloud info from store
          const dialogData = this.$store.getters.GET_CROPPED_MEASUREMENT_DIALOG;
          const pointCloudName = dialogData?.pointCloudName || 'Unknown';
          const pointCloudId = dialogData?.pointCloudId || null;

          // Get the 3D model global offset from viewer
          let modelGlobalOffset = dialogData.modelGlobalOffset;
          try {
            if (this.$store.state.viewer?.model?.getGlobalOffset) {
              const offset = this.$store.state.viewer.model.getGlobalOffset();
              modelGlobalOffset = {
                x: offset.x,
                y: offset.y,
                z: offset.z
              };
              debugLog('[App] 📍 Model Global Offset:', modelGlobalOffset);
            }
          } catch (error) {
            debugWarn('[App] Could not get model global offset:', error);
          }

          // Get the current crop box from the scene (user may have adjusted it)
          let updatedForgeCropBox = dialogData?.cropBox; // fallback to initial crop box
          try {
            if (window.AVA_POTREE_VIEWER) {
              const cropVolume = getActiveCropBoxVolume(window.AVA_POTREE_VIEWER);
              if (cropVolume) {
                // Convert BoxVolume to Box3 using the model's global offset
                const offset = modelGlobalOffset 
                  ? new THREE.Vector3(modelGlobalOffset.x, modelGlobalOffset.y, modelGlobalOffset.z)
                  : new THREE.Vector3(0, 0, 0);
                
                const forgeBox = getBox3FromBoxVolume(cropVolume, offset);
                updatedForgeCropBox = {
                  min: { x: forgeBox.min.x, y: forgeBox.min.y, z: forgeBox.min.z },
                  max: { x: forgeBox.max.x, y: forgeBox.max.y, z: forgeBox.max.z }
                };
                debugLog('[App] 📦 Updated Forge Crop Box from scene:', updatedForgeCropBox);
              }
            }
          } catch (error) {
            debugWarn('[App] Could not get updated crop box from scene:', error);
          }

          // Construct the complete analysis result JSON
          const analysisResultJson = {
            pointCloudData: {
              ...dialogData,
              cropBox: updatedForgeCropBox,
              modelGlobalOffset: modelGlobalOffset
            },
            statistics: this.corrosionState.statistics,
            cylinderData: this.corrosionState.cylinderData,
            thresholds: this.corrosionState.thresholds,
            timestamp: new Date().toISOString(),
            clipVolumeActive: this.corrosionState.clipVolumeActive,
            // Lasso selections for filtering
            lassoCumulativeMode: this.lassoCumulativeMode,
            lassoSelections: this.lassoHistory.map(lasso => ({
              id: lasso.id,
              type: lasso.type,  // 'filter' | 'clean'
              polygon: lasso.polygon,
              format: lasso.format,  // NDC format flag for viewport independence
              cameraState: lasso.cameraState,
              filterMode: lasso.filterMode,
              timestamp: lasso.timestamp,
              originalViewport: lasso.originalViewport  // Original viewport for debugging
            }))
          };

          // Emit event to parent with the analysis data and heatMapUrl
          this.$emit('create-inspect-tag', {
            analysisResultJson,
            heatMapUrl
          });

        } catch (error) {
          debugError('[App] Error creating inspect tag:', error);
          if (this.$dialog && this.$dialog.notify) {
            this.$dialog.notify.error('Failed to create inspect tag', {
              position: 'top-right',
              timeout: 3000,
            });
          }
        }
      },

      handleUpdateInspect({ tagId }) {
        debugLog('[App] Update requested for AVA Inspect tag:', tagId);

        // Show confirmation dialog before proceeding
        this.$dialog.confirm({
          title: 'Update AVA Inspect Tag',
          text: 'Are you sure you want to update this tag with the new analysis results? This will overwrite the existing data.',
          color: 'warning',
          actions: {
            false: 'Cancel',
            true: {
              text: 'Update',
              color: 'success',
              handle: async () => {
                try {
                  // Capture heatmap snapshot
                  debugLog('[App] 📸 Capturing heatmap...');
                  const snapshotBase64 = this.$refs.potreeViewer?.captureSnapshot();
                  let heatMapUrl = null;

                  if (snapshotBase64) {
                    heatMapUrl = await this.uploadHeatmapSnapshot(snapshotBase64, tagId);

                    if (!heatMapUrl) {
                      debugWarn('[App] Failed to upload heatmap, continuing without it');
                    }
                  }

                  // Get point cloud info from store
                  const dialogData = this.$store.getters.GET_CROPPED_MEASUREMENT_DIALOG;
                  
                  // Get existing analysis result if available (when updating from saved analysis)
                  const existingAnalysisResult = dialogData?.analysisResult;
                  const existingPointCloudData = existingAnalysisResult?.pointCloudData;

                  // Get the 3D model global offset from viewer (if available)
                  let modelGlobalOffset = null;
                  try {
                    if (this.$store.state.viewer?.model?.getGlobalOffset) {
                      const offset = this.$store.state.viewer.model.getGlobalOffset();
                      modelGlobalOffset = {
                        x: offset.x,
                        y: offset.y,
                        z: offset.z
                      };
                      debugLog('[App] 📍 Model Global Offset from viewer:', modelGlobalOffset);
                    } else if (existingPointCloudData?.modelGlobalOffset) {
                      // Fallback: use the saved model global offset from existing analysis
                      modelGlobalOffset = existingPointCloudData.modelGlobalOffset;
                      debugLog('[App] 📍 Using saved Model Global Offset from analysis result:', modelGlobalOffset);
                    }
                  } catch (error) {
                    debugWarn('[App] Could not get model global offset from viewer:', error);
                    // Try fallback to saved data
                    if (existingPointCloudData?.modelGlobalOffset) {
                      modelGlobalOffset = existingPointCloudData.modelGlobalOffset;
                      debugLog('[App] 📍 Fallback to saved Model Global Offset:', modelGlobalOffset);
                    }
                  }

                  // Get the current crop box from the scene (user may have adjusted it)
                  let updatedForgeCropBox = dialogData?.cropBox ? {
                    min: { x: dialogData.cropBox.min.x, y: dialogData.cropBox.min.y, z: dialogData.cropBox.min.z },
                    max: { x: dialogData.cropBox.max.x, y: dialogData.cropBox.max.y, z: dialogData.cropBox.max.z }
                  } : null; // fallback to initial crop box
                  
                  try {
                    if (window.AVA_POTREE_VIEWER) {
                      const cropVolume = getActiveCropBoxVolume(window.AVA_POTREE_VIEWER);
                      if (cropVolume) {
                        // Convert BoxVolume to Box3 using the model's global offset
                        const offset = modelGlobalOffset 
                          ? new THREE.Vector3(modelGlobalOffset.x, modelGlobalOffset.y, modelGlobalOffset.z)
                          : new THREE.Vector3(0, 0, 0);
                        
                        const forgeBox = getBox3FromBoxVolume(cropVolume, offset);
                        updatedForgeCropBox = {
                          min: { x: forgeBox.min.x, y: forgeBox.min.y, z: forgeBox.min.z },
                          max: { x: forgeBox.max.x, y: forgeBox.max.y, z: forgeBox.max.z }
                        };
                        debugLog('[App] 📦 Updated Forge Crop Box from scene:', updatedForgeCropBox);
                      } else if (existingPointCloudData?.cropBox) {
                        // Fallback: use the saved crop box from existing analysis
                        updatedForgeCropBox = existingPointCloudData.cropBox;
                        debugLog('[App] 📦 Using saved Forge Crop Box from analysis result:', updatedForgeCropBox);
                      }
                    } else if (existingPointCloudData?.cropBox) {
                      // Viewer not available, use the saved crop box from existing analysis
                      updatedForgeCropBox = existingPointCloudData.cropBox;
                      debugLog('[App] 📦 Viewer not available, using saved Forge Crop Box:', updatedForgeCropBox);
                    }
                  } catch (error) {
                    debugWarn('[App] Could not get updated crop box from scene:', error);
                    // Try fallback to saved data
                    if (existingPointCloudData?.cropBox) {
                      updatedForgeCropBox = existingPointCloudData.cropBox;
                      debugLog('[App] 📦 Fallback to saved Forge Crop Box:', updatedForgeCropBox);
                    }
                  }

                  // Construct the complete analysis result JSON
                  const analysisResultJson = {
                    pointCloudData: {
                      pointCloudUrl: dialogData.pointCloudUrl,
                      pointCloudName: dialogData.pointCloudName,
                      pointCloudId: dialogData.pointCloudId,
                      cropBox: updatedForgeCropBox,
                      forgePCInstance: dialogData.forgePCInstance ? {
                        position: dialogData.forgePCInstance.position,
                        quaternion: dialogData.forgePCInstance.quaternion,
                        scale: dialogData.forgePCInstance.scale,
                        pcoGeometry: dialogData.forgePCInstance.pcoGeometry
                      } : null,
                      unitScale: dialogData.unitScale,
                      globalOffset: dialogData.globalOffset,
                      modelGlobalOffset: modelGlobalOffset
                    },
                    statistics: this.corrosionState.statistics,
                    cylinderData: this.corrosionState.cylinderData,
                    thresholds: this.corrosionState.thresholds,
                    timestamp: new Date().toISOString(),
                    clipVolumeActive: this.corrosionState.clipVolumeActive,
                    // Lasso selections for filtering
                    lassoCumulativeMode: this.lassoCumulativeMode,
                    lassoSelections: this.lassoHistory.map(lasso => ({
                      id: lasso.id,
                      type: lasso.type,  // 'filter' | 'clean'
                      polygon: lasso.polygon,
                      format: lasso.format,  // NDC format flag for viewport independence
                      cameraState: lasso.cameraState,
                      filterMode: lasso.filterMode,
                      timestamp: lasso.timestamp,
                      originalViewport: lasso.originalViewport  // Original viewport for debugging
                    }))
                  };

                  debugLog('[App] Updating AVA Inspect tag:', tagId);
                  debugLog('[App] Update payload:', {
                    tagId,
                    hasStatistics: !!analysisResultJson.statistics,
                    hasCylinderData: !!analysisResultJson.cylinderData,
                    hasThresholds: !!analysisResultJson.thresholds,
                    hasHeatMapUrl: !!heatMapUrl
                  });

                  // Emit event to parent with the tagId, updated analysis data, and heatMapUrl
                  this.$emit('update-inspect-tag', {
                    tagId,
                    analysisResultJson,
                    heatMapUrl
                  });

                } catch (error) {
                  debugError('[App] Error updating inspect tag:', error);
                  if (this.$dialog && this.$dialog.notify) {
                    this.$dialog.notify.error('Failed to update inspect tag', {
                      position: 'top-right',
                      timeout: 3000,
                    });
                  }
                }
              }
            }
          }
        });
      },

      handleCloseCorrosionPanel() {
        debugLog('[App] Closing corrosion panel');
        // Optionally disable corrosion visualization when panel is closed
        // this.corrosionState.enabled = false;
        // if (this.$refs.potreeViewer) {
        //   this.$refs.potreeViewer.disableCorrosionVisualization();
        // }
      },

      // Toolbar Action Methods
      setMeasurementTool(toolValue) {
        const toolTimer = startTimer(`Measurement Tool: ${toolValue}`);

        logEvent('Measurement Tool Selected', {
          tool: toolValue,
          viewerReady: this.isViewerReady(),
          pointCloudReady: this.isPointCloudReady(),
        });

        const viewer = this.getViewer();
        if (!viewer) {
          debugWarn('Viewer not available for measurement tool:', toolValue);
          return;
        }

        if (!this.isViewerReady() || !this.isPointCloudReady()) {
          debugWarn('Viewer or point cloud not ready for measurement tool:', toolValue);
          return;
        }

        try {
          // Clear any existing tool state
          if (viewer.setTool) {
            viewer.setTool(null);
          }

          let measurement = null;
          let toolActivated = false;

          // Create measurement based on tool type using correct Potree API
          switch (toolValue) {
            case 'point':
              if (viewer.measuringTool) {
                measurement = viewer.measuringTool.startInsertion({
                  showDistances: false,
                  showAngles: false,
                  showCoordinates: true,
                  showArea: false,
                  closed: true,
                  maxMarkers: 1,
                  name: 'Point',
                });
                toolActivated = true;
              }
              break;

            case 'distance':
              if (viewer.measuringTool) {
                measurement = viewer.measuringTool.startInsertion({
                  showDistances: true,
                  showArea: false,
                  closed: false,
                  name: 'Distance',
                });
                toolActivated = true;
              }
              break;

            case 'area':
              if (viewer.measuringTool) {
                measurement = viewer.measuringTool.startInsertion({
                  showDistances: true,
                  showArea: true,
                  closed: true,
                  name: 'Area',
                });
                toolActivated = true;
              }
              break;

            case 'volume':
              if (viewer.volumeTool) {
                measurement = viewer.volumeTool.startInsertion();
                toolActivated = true;
              }
              break;

            case 'angle':
              if (viewer.measuringTool) {
                measurement = viewer.measuringTool.startInsertion({
                  showDistances: false,
                  showAngles: true,
                  showArea: false,
                  closed: true,
                  maxMarkers: 3,
                  name: 'Angle',
                });
                toolActivated = true;
              }
              break;

            case 'height':
              if (viewer.measuringTool) {
                measurement = viewer.measuringTool.startInsertion({
                  showDistances: false,
                  showHeight: true,
                  showArea: false,
                  closed: false,
                  maxMarkers: 2,
                  name: 'Height',
                });
                toolActivated = true;
              }
              break;

            case 'circle':
              if (viewer.measuringTool) {
                measurement = viewer.measuringTool.startInsertion({
                  showDistances: false,
                  showHeight: false,
                  showArea: false,
                  showCircle: true,
                  showEdges: false,
                  closed: false,
                  maxMarkers: 3,
                  name: 'Circle',
                });
                toolActivated = true;
              }
              break;

            case 'profile':
              if (viewer.profileTool) {
                measurement = viewer.profileTool.startInsertion();
                toolActivated = true;
              }
              break;

            case 'annotation':
              if (viewer.annotationTool) {
                measurement = viewer.annotationTool.startInsertion();
                toolActivated = true;
              }
              break;

            case 'fit-cylinder':
              this.activateCylinderMode();
              toolActivated = true;
              break;

            case 'lasso':
              this.activateLassoTool();
              toolActivated = true;
              break;

            default:
              // Deactivate tool
              toolActivated = true;
              break;
          }

          if (toolActivated) {
            if (measurement) {
              this.addMeasurement(toolValue, measurement);
            }

            logEvent('Measurement Tool Activated', {
              tool: toolValue,
              hasMeasurement: !!measurement,
            });

            toolTimer.end('Tool Activated');

            this.$emit('tool-changed', { tool: toolValue, measurement });
          } else {
            debugWarn('Could not activate measurement tool:', toolValue);
            // Reset the UI selection if tool failed to activate
            this.activeMeasurementTool = null;
          }
        } catch (error) {
          debugError('Error setting measurement tool:', error);
          // Reset the UI selection on error
          this.activeMeasurementTool = null;
        }
      },

      // Measurement Management Methods
      addMeasurement(tool, measurement) {
        if (measurement) {
          // Note: Circle completion tracking is now handled in activateCircleTool()
          // using marker_added event, which fires as each point is placed

          const measurementItem = {
            id: measurement.uuid || Date.now(),
            type: tool,
            measurement: measurement,
            timestamp: new Date(),
            displayName: this.generateMeasurementName({ type: tool, measurement }),
            icon: this.getMeasurementIcon(tool),
            values: this.extractMeasurementValues({ measurement, type: tool }),
            expanded: false, // For compact mode collapsible functionality
          };

          this.activeMeasurements.push(measurementItem);
          this.currentMeasurement = measurement;

          // Add event listeners for measurement completion
          if (measurement.addEventListener) {
            measurement.addEventListener('measurement_added', () => {
              debugLog('Measurement completed:', measurement);
              // Update values when measurement is completed
              measurementItem.values = this.extractMeasurementValues({ measurement, type: tool });

              // Force reactivity update for the UI
              this.$forceUpdate();

              // Auto-deactivate tool after measurement completion
              this.autoDeactivateTool();

              this.$emit('measurement-completed', measurement);
            });

            measurement.addEventListener('measurement_removed', () => {
              this.removeMeasurement(measurement);
            });

            // Live update panel values while editing/moving markers
            const refreshValues = () => {
              try {
                measurementItem.values = this.extractMeasurementValues({ measurement, type: tool });

                // Bump a reactive flag so compact value recomputes
                if (typeof measurementItem.version === 'undefined') {
                  this.$set(measurementItem, 'version', 1);
                } else {
                  this.$set(measurementItem, 'version', measurementItem.version + 1);
                }
                this.$forceUpdate();
              } catch (e) {
                debugWarn('Failed to refresh measurement values:', e);
              }
            };

            const updateEvents = [
              'marker_added',
              'marker_removed',
              'marker_moved',
              'marker_dropped',
              'measurement_changed',
              'change',
              'update',
            ];

            // Add circle-specific events for real-time updates
            if (tool === 'circle') {
              const circleEvents = [
                'circle_updated',
                'radius_changed',
                'center_moved',
                'points_changed',
                'circle_resized',
                'geometry_changed',
                'vertices_changed',
              ];

              circleEvents.forEach((evt) => {
                try {
                  measurement.addEventListener(evt, refreshValues);
                } catch (e) {
                  // Event not supported
                }
              });
            }

            updateEvents.forEach((evt) => {
              try {
                measurement.addEventListener(evt, refreshValues);
              } catch (e) {
                /* ignore */
              }
            });
          }

          this.$emit('measurement-started', measurement);
        }
      },

      removeMeasurement(measurement) {
        const index = this.activeMeasurements.findIndex(
          (m) => m.measurement === measurement || m.id === measurement.uuid,
        );
        if (index !== -1) {
          this.activeMeasurements.splice(index, 1);
          this.$emit('measurement-removed', measurement);
        }
      },

      removeSingleMeasurement(measurementItem) {
        debugLog(`[PotreeMeasurementsApp] Removing single measurement: ${measurementItem.type}`);
        const viewer = this.getViewer();

        // HANDLE FITTED-CYLINDER SEPARATELY (it has no measurement property)
        if (measurementItem.type === 'fitted-cylinder') {
          if (viewer && viewer.scene) {
            try {
              debugLog('[Cylinder] Removing fitted-cylinder:', measurementItem.id);

              // 1. Remove cylinder mesh with proper disposal
              if (viewer.scene.scene && measurementItem.mesh) {
                debugLog('[Cylinder] Disposing cylinder mesh');

                // Dispose geometry
                if (measurementItem.mesh.geometry) {
                  measurementItem.mesh.geometry.dispose();
                }

                // Dispose material(s)
                if (measurementItem.mesh.material) {
                  if (Array.isArray(measurementItem.mesh.material)) {
                    measurementItem.mesh.material.forEach(mat => {
                      if (mat.dispose) mat.dispose();
                    });
                  } else if (measurementItem.mesh.material.dispose) {
                    measurementItem.mesh.material.dispose();
                  }
                }

                // Remove from scene
                viewer.scene.scene.remove(measurementItem.mesh);
                measurementItem.mesh = null;

                debugLog('[Cylinder] Mesh removed and disposed');
              }

              // 2. Remove associated circles from Potree scene AND measurements panel
              if (measurementItem.circles && Array.isArray(measurementItem.circles)) {
                debugLog('[Cylinder] Removing associated circles:', measurementItem.circles.length);
                measurementItem.circles.forEach((circle) => {
                  try {
                    // Remove from Potree scene
                    if (viewer.scene.removeMeasurement) {
                      viewer.scene.removeMeasurement(circle);
                    }

                    // Remove from activeMeasurements array (measurements panel)
                    const circleIndex = this.activeMeasurements.findIndex(
                      (m) => m.measurement === circle || m.id === circle.uuid
                    );

                    if (circleIndex !== -1) {
                      debugLog('[Cylinder] Removing circle from measurements panel, index:', circleIndex);
                      this.activeMeasurements.splice(circleIndex, 1);
                    }

                  } catch (circleError) {
                    debugWarn('[Cylinder] Error removing circle:', circleError);
                  }
                });
                measurementItem.circles = [];
              }

              // 3. Deselect if currently selected (clears Cylinder Views panel)
              if (this.selectedCylinder && this.selectedCylinder.id === measurementItem.id) {
                debugLog('[Cylinder] Deselecting removed cylinder');
                this.deselectCylinder();
              }

              // 4. Force scene render update
              if (viewer.renderer && viewer.scene) {
                viewer.renderer.render(viewer.scene.scene, viewer.scene.getActiveCamera());
                debugLog('[Cylinder] Scene render updated');
              }

            } catch (error) {
              debugError('[Cylinder] Error removing fitted-cylinder:', error);
            }
          }

          // Remove from activeMeasurements array
          const index = this.activeMeasurements.findIndex((m) => m.id === measurementItem.id);
          if (index !== -1) {
            this.activeMeasurements.splice(index, 1);
          }

          this.$emit('single-measurement-removed', measurementItem);
          return; // Early return - fitted-cylinder handled
        }

        // HANDLE SCREEN-LASSO (with lassoData property)
        if (measurementItem.type === 'screen-lasso') {
          debugLog('[PotreeMeasurementsApp] 🗑️  Removing screen-lasso:', measurementItem.lassoData.id);

          // Remove lasso filter
          this.removeLasso(measurementItem.lassoData.id);

          // Remove from activeMeasurements array
          const index = this.activeMeasurements.findIndex((m) => m.id === measurementItem.id);
          if (index !== -1) {
            this.activeMeasurements.splice(index, 1);
          }

          this.$emit('single-measurement-removed', measurementItem);
          return; // Early return - screen-lasso handled
        }

        // HANDLE REGULAR MEASUREMENTS (with measurement property)
        if (viewer && viewer.scene && measurementItem.measurement) {
          try {
            // Remove from Potree scene based on measurement type
            const measurement = measurementItem.measurement;

            switch (measurementItem.type) {
              case 'point':
              case 'distance':
              case 'area':
              case 'angle':
              case 'height':
              case 'circle':
                if (viewer.scene.removeMeasurement) {
                  viewer.scene.removeMeasurement(measurement);
                }
                break;
              case 'volume':
                if (viewer.scene.removeVolume) {
                  viewer.scene.removeVolume(measurement);
                }
                break;
              case 'profile':
                if (viewer.scene.removeProfile) {
                  viewer.scene.removeProfile(measurement);
                }
                break;
              case 'annotation':
                if (viewer.scene.removeAnnotation) {
                  viewer.scene.removeAnnotation(measurement);
                }
                break;
            }
          } catch (error) {
            debugWarn('Error removing single measurement from scene:', error);
          }
        }

        // Remove from Vue component state (for regular measurements)
        this.removeMeasurement(measurementItem.measurement);
        this.$emit('single-measurement-removed', measurementItem);
      },

      // Cylinder Selection Methods
      selectCylinder(measurementItem) {
        if (!measurementItem) {
          this.selectedCylinder = null;
          return;
        }

        if (measurementItem.type === 'fitted-cylinder') {
          this.selectedCylinder = measurementItem;
          this.$emit('cylinder-selected', measurementItem);
          debugLog('Cylinder selected:', measurementItem);
        } else {
          // Deselect if non-cylinder clicked
          this.selectedCylinder = null;
        }
      },

      deselectCylinder() {
        if (this.selectedCylinder) {
          this.$emit('cylinder-deselected', this.selectedCylinder);
        }
        this.selectedCylinder = null;
      },

      handleMeasurementClick(measurementItem) {
        // Select cylinder if it's a fitted-cylinder type
        if (measurementItem.type === 'fitted-cylinder') {
          this.selectCylinder(measurementItem);
        } else {
          // Deselect cylinder if another measurement is clicked
          this.deselectCylinder();
        }
      },

      // Measurement Helper Methods
      generateMeasurementName(item) {
        if (!item || !item.type) return 'Unknown';

        const typeNames = {
          point: 'Point',
          distance: 'Distance',
          area: 'Area',
          volume: 'Volume',
          angle: 'Angle',
          height: 'Height',
          circle: 'Circle',
          profile: 'Profile',
          annotation: 'Annotation',
        };

        // Special handling for screen-lasso to show Clean Lasso vs Lasso
        if (item.type === 'screen-lasso' && item.lassoData) {
          const isClean = item.lassoData.type === 'clean';
          const baseName = isClean ? 'Clean' : 'Lasso';
          // Count by lasso type for proper indexing
          const sameTypeLassos = this.activeMeasurements.filter(
            m => m.type === 'screen-lasso' &&
                 m.lassoData &&
                 (m.lassoData.type || 'filter') === (item.lassoData.type || 'filter')
          );
          const index = sameTypeLassos.findIndex(m => m.id === item.id);
          return `${baseName} ${index + 1}`;
        }

        const baseName = typeNames[item.type] || 'Measurement';
        const index = this.activeMeasurements
          .filter((m) => m.type === item.type)
          .findIndex((m) => m.id === item.id);

        return `${baseName} ${index + 1}`;
      },

      getMeasurementIcon(type) {
        const iconMap = {
          point: 'mdi-map-marker',
          distance: 'mdi-ruler',
          area: 'mdi-vector-polygon',
          volume: 'mdi-cube-outline',
          angle: 'mdi-angle-acute',
          height: 'mdi-arrow-expand-vertical',
          circle: 'mdi-circle-outline',
          'fitted-cylinder': 'mdi-cylinder',
          profile: 'mdi-chart-line',
          annotation: 'mdi-comment-text',
          'screen-lasso': 'mdi-lasso',
        };
        return iconMap[type] || 'mdi-ruler-square-compass';
      },

      // Helper function to extract point coordinates from various possible data structures
      getPointCoordinates(measurement) {
        // Try standard points array
        if (
          measurement.points &&
          Array.isArray(measurement.points) &&
          measurement.points.length > 0
        ) {
          const firstPoint = measurement.points[0];
          if (firstPoint && firstPoint.position) {
            const pos = firstPoint.position;
            return {
              label: 'Coordinates',
              text: `X: ${pos.x.toFixed(3)}, Y: ${pos.y.toFixed(3)}, Z: ${pos.z.toFixed(3)}`,
            };
          } else if (firstPoint && typeof firstPoint.x !== 'undefined') {
            return {
              label: 'Coordinates',
              text: `X: ${firstPoint.x.toFixed(3)}, Y: ${firstPoint.y.toFixed(
                3,
              )}, Z: ${firstPoint.z.toFixed(3)}`,
            };
          }
        }

        // Try markers array (alternative Potree structure)
        if (
          measurement.markers &&
          Array.isArray(measurement.markers) &&
          measurement.markers.length > 0
        ) {
          const marker = measurement.markers[0];
          if (marker && marker.position) {
            const pos = marker.position;
            return {
              label: 'Coordinates',
              text: `X: ${pos.x.toFixed(3)}, Y: ${pos.y.toFixed(3)}, Z: ${pos.z.toFixed(3)}`,
            };
          }
        }

        // Try coordinates array
        if (
          measurement.coordinates &&
          Array.isArray(measurement.coordinates) &&
          measurement.coordinates.length > 0
        ) {
          const coord = measurement.coordinates[0];
          if (coord && typeof coord.x !== 'undefined') {
            return {
              label: 'Coordinates',
              text: `X: ${coord.x.toFixed(3)}, Y: ${coord.y.toFixed(3)}, Z: ${coord.z.toFixed(3)}`,
            };
          }
        }

        // If no coordinates found, return status
        return { label: 'Status', text: 'Placing point...' };
      },

      // Calculate circumcenter of a circle from 3 points on the circumference
      calculateCircumcenter(p1, p2, p3) {
        // Convert points to Vector3 format if needed
        const vec1 = p1.position ? p1.position : p1;
        const vec2 = p2.position ? p2.position : p2;
        const vec3 = p3.position ? p3.position : p3;

        // Check for valid input points
        if (
          !vec1 ||
          !vec2 ||
          !vec3 ||
          !Number.isFinite(vec1.x) ||
          !Number.isFinite(vec2.x) ||
          !Number.isFinite(vec3.x)
        ) {
          return null;
        }

        // Check if points are collinear (would not form a valid circle)
        const cross = {
          x: (vec2.y - vec1.y) * (vec3.z - vec1.z) - (vec2.z - vec1.z) * (vec3.y - vec1.y),
          y: (vec2.z - vec1.z) * (vec3.x - vec1.x) - (vec2.x - vec1.x) * (vec3.z - vec1.z),
          z: (vec2.x - vec1.x) * (vec3.y - vec1.y) - (vec2.y - vec1.y) * (vec3.x - vec1.x),
        };

        const crossMagnitude = Math.sqrt(cross.x * cross.x + cross.y * cross.y + cross.z * cross.z);
        if (crossMagnitude < 1e-10) {
          // Points are collinear, cannot form a circle
          return null;
        }

        // Calculate circumcenter using perpendicular bisectors method for 3D
        const a = {
          x: vec2.x - vec1.x,
          y: vec2.y - vec1.y,
          z: vec2.z - vec1.z,
        };

        const b = {
          x: vec3.x - vec1.x,
          y: vec3.y - vec1.y,
          z: vec3.z - vec1.z,
        };

        const aSqr = a.x * a.x + a.y * a.y + a.z * a.z;
        const bSqr = b.x * b.x + b.y * b.y + b.z * b.z;
        const ab2 = 2 * (a.x * b.x + a.y * b.y + a.z * b.z);

        const denom = 2 * (aSqr * bSqr - (ab2 * ab2) / 4);

        if (Math.abs(denom) < 1e-10) {
          return null;
        }

        const alpha = (bSqr * (aSqr - ab2 / 2)) / denom;
        const beta = (aSqr * (bSqr - ab2 / 2)) / denom;

        const center = {
          x: vec1.x + alpha * a.x + beta * b.x,
          y: vec1.y + alpha * a.y + beta * b.y,
          z: vec1.z + alpha * a.z + beta * b.z,
        };

        return center;
      },

      // Calculate circle radius from center point to any circumference point
      calculateCircleRadius(center, circumferencePoint) {
        if (!center || !circumferencePoint) return 0;

        const cp = circumferencePoint.position ? circumferencePoint.position : circumferencePoint;

        if (!Number.isFinite(center.x) || !Number.isFinite(cp.x)) return 0;

        const dx = cp.x - center.x;
        const dy = cp.y - center.y;
        const dz = cp.z - center.z;

        return Math.sqrt(dx * dx + dy * dy + dz * dz);
      },

      // Enhanced circle measurement value extraction with multiple fallback methods
      getCircleMeasurementValue(measurement) {
        try {
          let centerText = '';
          let radiusText = '';
          let points = [];

          // Method 1: Try standard points array
          if (measurement.points && Array.isArray(measurement.points)) {
            points = measurement.points;
          }

          // Method 2: Try markers array (alternative Potree structure)
          else if (measurement.markers && Array.isArray(measurement.markers)) {
            points = measurement.markers;
          }

          // Method 3: Try geometry or shape properties
          else if (measurement.geometry && measurement.geometry.vertices) {
            points = measurement.geometry.vertices;
          }

          // Calculate true circle center and radius using 3-point geometry
          let centerPos = null;
          let radiusM = 0;

          // For circles, we need 3 points on the circumference to calculate the true center
          if (points.length >= 3) {
            // Calculate circumcenter from the 3 circumference points
            centerPos = this.calculateCircumcenter(points[0], points[1], points[2]);

            if (centerPos) {
              centerText = `(${centerPos.x.toFixed(2)}, ${centerPos.y.toFixed(
                2,
              )}, ${centerPos.z.toFixed(2)})`;

              // Calculate radius from center to any circumference point
              radiusM = this.calculateCircleRadius(centerPos, points[0]);
            }
          }

          // Fallback methods for incomplete circles or legacy data
          if (!centerPos || radiusM === 0) {
            // Method 1: Direct getRadius method
            if (measurement.getRadius && typeof measurement.getRadius === 'function') {
              radiusM = measurement.getRadius();
            }

            // Method 2: Try radius property
            else if (measurement.radius && Number.isFinite(measurement.radius)) {
              radiusM = measurement.radius;
            }

            // Method 3: For incomplete circles (less than 3 points), use first point as temporary center
            else if (points.length >= 1) {
              const tempCenter = points[0] && points[0].position ? points[0].position : points[0];
              if (tempCenter && Number.isFinite(tempCenter.x)) {
                centerPos = tempCenter;
                centerText = `(${tempCenter.x.toFixed(2)}, ${tempCenter.y.toFixed(
                  2,
                )}, ${tempCenter.z.toFixed(2)})`;

                // If we have a second point, calculate distance as temporary radius
                if (points.length >= 2) {
                  const edge = points[1] && points[1].position ? points[1].position : points[1];
                  if (edge && Number.isFinite(edge.x)) {
                    radiusM = this.calculateCircleRadius(tempCenter, edge);
                  }
                }
              }
            }

            // Method 4: Try getTotalDistance (sometimes used for diameter)
            else if (
              measurement.getTotalDistance &&
              typeof measurement.getTotalDistance === 'function'
            ) {
              const distance = measurement.getTotalDistance();
              if (distance > 0) {
                radiusM = distance / 2; // Assume it's diameter
              }
            }
          }

          // Format radius text
          if (Number.isFinite(radiusM) && radiusM > 0) {
            // Convert to appropriate units
            if (radiusM < 0.001) {
              // Less than 1mm, show in micrometers
              radiusText = `R: ${(radiusM * 1000000).toFixed(0)} μm`;
            } else if (radiusM < 1) {
              // Less than 1m, show in millimeters
              radiusText = `R: ${(radiusM * 1000).toFixed(1)} mm`;
            } else {
              // 1m or more, show in meters
              radiusText = `R: ${radiusM.toFixed(3)} m`;
            }
          }

          // Combine center and radius information
          let displayText = '';
          if (centerText && radiusText) {
            displayText = `${radiusText} at ${centerText}`;
          } else if (radiusText) {
            displayText = radiusText;
          } else if (centerText) {
            displayText = `Center: ${centerText}`;
          } else if (points.length > 0) {
            displayText = `${points.length} points`;
          } else {
            displayText = 'Drawing circle...';
          }

          return { label: 'Circle', text: displayText };
        } catch (error) {
          return { label: 'Circle', text: 'Error reading circle data' };
        }
      },

      // Get the most relevant single value for compact display
      getCompactMeasurementValue(item) {
        if (!item || !item.measurement) return { label: 'Status', text: 'No data' };

        const measurement = item.measurement;

        try {
          // Return the most relevant value based on measurement type
          switch (item.type) {
            case 'distance':
              if (
                measurement.getTotalDistance &&
                typeof measurement.getTotalDistance === 'function'
              ) {
                const distance = measurement.getTotalDistance();
                if (distance > 0) {
                  return { label: 'Distance', text: `${distance.toFixed(2)} m` };
                }
              }
              break;

            case 'area':
              if (measurement.getArea && typeof measurement.getArea === 'function') {
                const area = measurement.getArea();
                if (area > 0) {
                  return { label: 'Area', text: `${area.toFixed(2)} m²` };
                }
              }
              break;

            case 'volume':
              if (measurement.getVolume && typeof measurement.getVolume === 'function') {
                const volume = measurement.getVolume();
                if (volume > 0) {
                  return { label: 'Volume', text: `${volume.toFixed(2)} m³` };
                }
              }
              break;

            case 'angle':
              if (measurement.getAngle && typeof measurement.getAngle === 'function') {
                const angle = measurement.getAngle();
                if (angle !== undefined) {
                  return { label: 'Angle', text: `${angle.toFixed(1)}°` };
                }
              }
              break;

            case 'height':
              if (measurement.getHeight && typeof measurement.getHeight === 'function') {
                const height = measurement.getHeight();
                if (height !== undefined) {
                  return { label: 'Height', text: `${height.toFixed(2)} m` };
                }
              }
              break;

            case 'circle':
              // Enhanced circle value extraction with multiple fallback methods
              return this.getCircleMeasurementValue(measurement);

            case 'point':
              return this.getPointCoordinates(measurement);

            case 'profile':
              if (measurement.points && Array.isArray(measurement.points)) {
                return { label: 'Points', text: `${measurement.points.length} pts` };
              }
              break;

            case 'annotation':
              return { label: 'Note', text: 'Annotation' };

            case 'fitted-cylinder':
              if (item.cylinderData) {
                const { radius, height } = item.cylinderData;
                return {
                  label: 'Cylinder',
                  text: `R: ${radius.toFixed(2)}m, H: ${height.toFixed(2)}m`,
                };
              }
              return { label: 'Cylinder', text: 'Processing...' };

            case 'screen-lasso':
              if (item.lassoData) {
                const mode = item.lassoData.filterMode.toUpperCase();
                const icon = item.lassoData.filterMode === 'inside' ? '●' : '○';
                const points = item.lassoData.pointsFiltered || 0;
                const isActive = this.activeLassoIds.includes(item.lassoData.id);
                const activeIcon = isActive ? '✓' : '○';
                return {
                  label: `${activeIcon} ${icon} ${mode}`,
                  text: `${points.toLocaleString()} pts`
                };
              }
              return { label: 'Lasso', text: 'Processing...' };

            default:
              // Try to get the most common measurement
              if (
                measurement.getTotalDistance &&
                typeof measurement.getTotalDistance === 'function'
              ) {
                const distance = measurement.getTotalDistance();
                if (distance > 0) {
                  return { label: 'Distance', text: `${distance.toFixed(2)} m` };
                }
              }
              break;
          }

          return { label: 'Status', text: 'In progress' };
        } catch (error) {
          debugWarn('Error extracting compact measurement value:', error);
          return { label: 'Status', text: 'Error' };
        }
      },

      extractMeasurementValues(item) {
        if (!item || !item.measurement) return [];

        const measurement = item.measurement;
        const values = [];

        try {
          // Extract common properties
          if (measurement.getTotalDistance && typeof measurement.getTotalDistance === 'function') {
            const distance = measurement.getTotalDistance();
            if (distance > 0) {
              values.push({
                label: 'Distance',
                text: `${distance.toFixed(3)} m`,
              });
            }
          }

          if (measurement.getArea && typeof measurement.getArea === 'function') {
            const area = measurement.getArea();
            if (area > 0) {
              values.push({
                label: 'Area',
                text: `${area.toFixed(3)} m²`,
              });
            }
          }

          if (measurement.getVolume && typeof measurement.getVolume === 'function') {
            const volume = measurement.getVolume();
            if (volume > 0) {
              values.push({
                label: 'Volume',
                text: `${volume.toFixed(3)} m³`,
              });
            }
          }

          // Handle points array
          if (measurement.points && Array.isArray(measurement.points)) {
            const pointCount = measurement.points.length;
            if (pointCount > 0) {
              values.push({
                label: 'Points',
                text: `${pointCount}`,
              });

              // Add coordinates for single points
              if (pointCount === 1 && item.type === 'point') {
                const point = measurement.points[0];
                if (point && typeof point.x !== 'undefined') {
                  values.push({
                    label: 'X',
                    text: `${point.x.toFixed(3)}`,
                  });
                  values.push({
                    label: 'Y',
                    text: `${point.y.toFixed(3)}`,
                  });
                  values.push({
                    label: 'Z',
                    text: `${point.z.toFixed(3)}`,
                  });
                }
              }
            }
          }

          // Handle angle measurements
          if (
            item.type === 'angle' &&
            measurement.getAngle &&
            typeof measurement.getAngle === 'function'
          ) {
            const angle = measurement.getAngle();
            if (angle !== undefined) {
              values.push({
                label: 'Angle',
                text: `${angle.toFixed(2)}°`,
              });
            }
          }

          // Handle height measurements
          if (
            item.type === 'height' &&
            measurement.getHeight &&
            typeof measurement.getHeight === 'function'
          ) {
            const height = measurement.getHeight();
            if (height !== undefined) {
              values.push({
                label: 'Height',
                text: `${height.toFixed(3)} m`,
              });
            }
          }

          // Circle values intentionally omitted (debug-only mode)

          // Add timestamp
          if (item.timestamp) {
            values.push({
              label: 'Created',
              text: item.timestamp.toLocaleTimeString(),
            });
          }

          // Fallback for measurements with no extractable values
          if (values.length === 0) {
            values.push({
              label: 'Status',
              text: 'In progress',
            });
          }
        } catch (error) {
          debugWarn('Error extracting measurement values:', error);
          values.push({
            label: 'Status',
            text: 'Error reading values',
          });
        }

        return values;
      },

      // Enhanced Panel Methods

      toggleMeasurementsPanel() {
        this.showMeasurementsPanel = !this.showMeasurementsPanel;
      },

      toggleLabelsVisibility() {
        const viewer = this.getViewer();
        if (!viewer) {
          debugWarn('Viewer not available for labels visibility toggle');
          return;
        }

        try {
          this.labelsVisible = !this.labelsVisible;

          // Use Potree's built-in showLabels property for measuring tool
          if (viewer.measuringTool) {
            viewer.measuringTool.showLabels = this.labelsVisible;
          }

          debugLog('Labels visibility toggled to:', this.labelsVisible);
          this.$emit('labels-visibility-changed', this.labelsVisible);
        } catch (error) {
          debugError('Error toggling labels visibility:', error);
        }
      },

      // Sidebar Event Handlers
      handleSidebarToggle(collapsed) {
        debugLog('[PotreeMeasurementsApp] Sidebar toggle:', collapsed);
        // Can add any additional logic here if needed
      },

      handleSidebarResize(width) {
        debugLog('[PotreeMeasurementsApp] Sidebar resized to:', width);
        // Can store width in localStorage or vuex if needed
      },

      autoDeactivateTool() {
        // Deactivate the current measurement tool after successful measurement creation
        setTimeout(() => {
          this.activeMeasurementTool = null;
          debugLog('Measurement tool auto-deactivated');
        }, 500); // Small delay to ensure measurement is fully created
      },

      // Panel Dragging Methods
      startPanelDrag(event) {
        // Prevent dragging if clicking on buttons or other interactive elements
        if (event.target.closest('.v-btn') || event.target.closest('button')) {
          return;
        }

        this.panelDragging = true;
        this.dragOffset.x = event.clientX - this.panelPosition.x;
        this.dragOffset.y = event.clientY - this.panelPosition.y;

        // Prevent text selection and other default behaviors
        event.preventDefault();
        event.stopPropagation();

        // Add temporary styles to improve dragging experience
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'grabbing';
      },

      handlePanelDrag(event) {
        if (!this.panelDragging) return;

        event.preventDefault();
        event.stopPropagation();

        const newX = event.clientX - this.dragOffset.x;
        const newY = event.clientY - this.dragOffset.y;

        // Constrain panel within viewport with better boundaries
        const panelWidth = 320;
        const panelHeight = Math.min(400, window.innerHeight - 100); // Dynamic height based on viewport
        const margin = 10;
        const maxX = window.innerWidth - panelWidth - margin;
        const maxY = window.innerHeight - panelHeight - margin;

        this.panelPosition.x = Math.max(margin, Math.min(maxX, newX));
        this.panelPosition.y = Math.max(margin, Math.min(maxY, newY));
      },

      stopPanelDrag(event) {
        if (this.panelDragging) {
          this.panelDragging = false;
          this.savePanelPosition();

          // Restore body styles
          document.body.style.userSelect = '';
          document.body.style.cursor = '';

          if (event) {
            event.preventDefault();
            event.stopPropagation();
          }
        }
      },

      // Panel Position Persistence
      loadPanelPosition() {
        // Simple default position relative to dialog container
        this.panelPosition = { x: 20, y: 100 };
      },

      savePanelPosition() {
        try {
          const saveData = {
            position: { ...this.panelPosition },
          };
          localStorage.setItem('potree-measurements-panel', JSON.stringify(saveData));
        } catch (error) {
          debugWarn('Could not save panel position:', error);
        }
      },

      // Corrosion Panel Dragging Methods
      startCorrosionPanelDrag(dragData) {
        this.corrosionPanelDragging = true;
        this.corrosionDragOffset.x = dragData.x - this.corrosionPanelPosition.x;
        this.corrosionDragOffset.y = dragData.y - this.corrosionPanelPosition.y;

        // Prevent text selection and improve dragging experience
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'grabbing';
      },

      handleCorrosionPanelDrag(event) {
        if (!this.corrosionPanelDragging) return;

        event.preventDefault();
        event.stopPropagation();

        const newX = event.clientX - this.corrosionDragOffset.x;
        const newY = event.clientY - this.corrosionDragOffset.y;

        // Constrain panel within viewport with sidebar awareness
        const panelWidth = 450; // Corrosion panel is wider than measurements panel
        const panelHeight = Math.min(400, window.innerHeight - 100);

        // Use LayoutService to constrain position with sidebar awareness
        const constrained = this.layoutService.constrainPanelPosition(
          newX, newY, panelWidth, panelHeight, 10
        );

        this.corrosionPanelPosition.x = constrained.x;
        this.corrosionPanelPosition.y = constrained.y;
      },

      stopCorrosionPanelDrag(event) {
        if (this.corrosionPanelDragging) {
          this.corrosionPanelDragging = false;
          this.saveCorrosionPanelPosition();

          // Restore body styles
          document.body.style.userSelect = '';
          document.body.style.cursor = '';

          if (event) {
            event.preventDefault();
            event.stopPropagation();
          }
        }
      },

      // Corrosion Panel Position Persistence
      loadCorrosionPanelPosition() {
        const panelWidth = 450;
        const panelHeight = 400;

        try {
          const saved = localStorage.getItem('potree-corrosion-panel');
          if (saved) {
            const { position } = JSON.parse(saved);
            if (position && typeof position.x === 'number' && typeof position.y === 'number') {
              // Validate saved position with sidebar awareness
              const safePosition = this.layoutService.getSafePosition(
                position.x,
                position.y,
                panelWidth,
                panelHeight
              );

              this.corrosionPanelPosition = {
                x: safePosition.x,
                y: safePosition.y
              };

              if (safePosition.adjusted) {
                debugLog('[PotreeMeasurementsApp] Corrosion panel position adjusted due to sidebar state change');
              }
              return;
            }
          }

          // Default position if no saved position or invalid position
          // Calculate initial position to match original CSS layout (right: 20px, top: 80px)
          const defaultPos = this.layoutService.getDefaultPanelPosition(
            panelWidth,
            panelHeight,
            20, // offsetFromRight
            80  // offsetFromTop
          );
          this.corrosionPanelPosition = defaultPos;
        } catch (error) {
          debugWarn('Could not load corrosion panel position:', error);
          // Calculate default position even on error
          const defaultPos = this.layoutService.getDefaultPanelPosition(
            panelWidth,
            panelHeight,
            20, // offsetFromRight
            80  // offsetFromTop
          );
          this.corrosionPanelPosition = defaultPos;
        }
      },

      saveCorrosionPanelPosition() {
        try {
          const saveData = {
            position: { ...this.corrosionPanelPosition },
          };
          localStorage.setItem('potree-corrosion-panel', JSON.stringify(saveData));
        } catch (error) {
          debugWarn('Could not save corrosion panel position:', error);
        }
      },

      // Cylinder Panel Dragging Methods
      startCylinderPanelDrag(dragData) {
        this.cylinderPanelDragging = true;
        this.cylinderDragOffset.x = dragData.x - this.cylinderPanelPosition.x;
        this.cylinderDragOffset.y = dragData.y - this.cylinderPanelPosition.y;

        // Prevent text selection and improve dragging experience
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'grabbing';
      },

      handleCylinderPanelDrag(event) {
        if (!this.cylinderPanelDragging) return;

        event.preventDefault();
        event.stopPropagation();

        const newX = event.clientX - this.cylinderDragOffset.x;
        const newY = event.clientY - this.cylinderDragOffset.y;

        // Constrain panel within viewport with sidebar awareness
        const panelWidth = 340; // Cylinder panel width
        const panelHeight = Math.min(400, window.innerHeight - 100);

        // Use LayoutService to constrain position with sidebar awareness
        const constrained = this.layoutService.constrainPanelPosition(
          newX, newY, panelWidth, panelHeight, 10
        );

        this.cylinderPanelPosition.x = constrained.x;
        this.cylinderPanelPosition.y = constrained.y;
      },

      stopCylinderPanelDrag(event) {
        if (this.cylinderPanelDragging) {
          this.cylinderPanelDragging = false;
          this.saveCylinderPanelPosition();

          // Restore body styles
          document.body.style.userSelect = '';
          document.body.style.cursor = '';

          if (event) {
            event.preventDefault();
            event.stopPropagation();
          }
        }
      },

      // Cylinder Panel Position Persistence
      loadCylinderPanelPosition() {
        const panelWidth = 340;
        const panelHeight = 400;

        try {
          const saved = localStorage.getItem('potree-cylinder-panel');
          if (saved) {
            const { position } = JSON.parse(saved);
            if (position && typeof position.x === 'number' && typeof position.y === 'number') {
              // Validate saved position with sidebar awareness
              const safePosition = this.layoutService.getSafePosition(
                position.x,
                position.y,
                panelWidth,
                panelHeight
              );

              this.cylinderPanelPosition = {
                x: safePosition.x,
                y: safePosition.y
              };

              if (safePosition.adjusted) {
                debugLog('[PotreeMeasurementsApp] Cylinder panel position adjusted due to sidebar state change');
              }
              return;
            }
          }

          // Default position if no saved position or invalid position
          // Calculate initial position to match original CSS layout (bottom: 120px, left: 120px)
          const viewport = this.layoutService.getAvailableViewport();
          this.cylinderPanelPosition = {
            x: viewport.left + 120, // offsetFromLeft
            y: window.innerHeight - panelHeight - 120 // offsetFromBottom
          };
        } catch (error) {
          debugWarn('Could not load cylinder panel position:', error);
          // Calculate default position even on error
          const viewport = this.layoutService.getAvailableViewport();
          this.cylinderPanelPosition = {
            x: viewport.left + 120, // offsetFromLeft
            y: window.innerHeight - panelHeight - 120 // offsetFromBottom
          };
        }
      },

      saveCylinderPanelPosition() {
        try {
          const saveData = {
            position: { ...this.cylinderPanelPosition },
          };
          localStorage.setItem('potree-cylinder-panel', JSON.stringify(saveData));
        } catch (error) {
          debugWarn('Could not save cylinder panel position:', error);
        }
      },

      clearAllMeasurements() {
        const measurementCount = this.activeMeasurements.length;

        logEvent('Clear All Measurements', {
          count: measurementCount,
        });

        const viewer = this.getViewer();
        if (viewer && viewer.scene) {
          try {
            // Clear measurements from Potree scene (measurements is an array)
            if (viewer.scene.measurements && Array.isArray(viewer.scene.measurements)) {
              // Create a copy to avoid modification during iteration
              const measurements = [...viewer.scene.measurements];
              measurements.forEach((measurement) => {
                try {
                  viewer.scene.removeMeasurement(measurement);
                } catch (error) {
                  debugWarn('Error removing measurement:', error);
                }
              });
            }

            // Clear profiles (profiles is an array)
            if (
              viewer.profileTool &&
              viewer.profileTool.profiles &&
              Array.isArray(viewer.profileTool.profiles)
            ) {
              const profiles = [...viewer.profileTool.profiles];
              profiles.forEach((profile) => {
                try {
                  viewer.scene.removeProfile(profile);
                } catch (error) {
                  debugWarn('Error removing profile:', error);
                }
              });
            }

            // Clear volumes (volumes is an array)
            if (viewer.scene.volumes && Array.isArray(viewer.scene.volumes)) {
              const volumes = [...viewer.scene.volumes];
              volumes.forEach((volume) => {
                try {
                  viewer.scene.removeVolume(volume);
                } catch (error) {
                  debugWarn('Error removing volume:', error);
                }
              });
            }

            // Clear annotations (annotations is a container object, not an array)
            if (viewer.scene.annotations) {
              try {
                // Annotations is a Three.js Object3D-like container
                if (
                  viewer.scene.annotations.children &&
                  Array.isArray(viewer.scene.annotations.children)
                ) {
                  // Create a copy since we'll be modifying the children array
                  const annotationChildren = [...viewer.scene.annotations.children];
                  annotationChildren.forEach((annotation) => {
                    try {
                      viewer.scene.removeAnnotation(annotation);
                    } catch (error) {
                      debugWarn('Error removing annotation:', error);
                    }
                  });
                }
              } catch (error) {
                debugWarn('Error clearing annotations:', error);
              }
            }
          } catch (error) {
            debugError('Error in clearAllMeasurements:', error);
          }
        }

        this.activeMeasurements = [];
        this.currentMeasurement = null;

        logEvent('Measurements Cleared', {
          previousCount: measurementCount,
        });

        this.$emit('measurements-cleared');
        debugLog('All measurements cleared');
      },

      getMeasurementCount() {
        return this.activeMeasurements.length;
      },

      getLastMeasurement() {
        return this.activeMeasurements[this.activeMeasurements.length - 1] || null;
      },

      fitToScreen() {
        if (this.$refs.potreeViewer) {
          this.$refs.potreeViewer.fitToScreen();
        }
      },

      setCameraView(view) {
        const viewer = this.getViewer();
        if (!viewer) {
          debugWarn('Viewer not available for camera view:', view);
          return;
        }

        if (!this.isViewerReady() || !this.isPointCloudReady()) {
          debugWarn('Viewer or point cloud not ready for camera view');
          return;
        }

        try {
          let camera, boundingBox, scene;

          // Try different ways to access camera and scene
          if (viewer.scene) {
            scene = viewer.scene;
            camera = scene.getActiveCamera ? scene.getActiveCamera() : scene.camera;
          } else {
            camera = viewer.camera;
          }

          // Try different ways to get bounding box
          if (viewer.getBoundingBox) {
            boundingBox = viewer.getBoundingBox();
          } else if (this.pointCloudInstance && this.pointCloudInstance.boundingBox) {
            boundingBox = this.pointCloudInstance.boundingBox;
          } else if (
            viewer.scene &&
            viewer.scene.pointclouds &&
            viewer.scene.pointclouds.length > 0
          ) {
            boundingBox = viewer.scene.pointclouds[0].boundingBox;
          }

          if (!camera) {
            debugWarn('Camera not available');
            return;
          }

          if (!boundingBox) {
            debugWarn('Bounding box not available for camera positioning');
            return;
          }

          const center = boundingBox.getCenter ? boundingBox.getCenter() : boundingBox.center;
          const size = boundingBox.getSize
            ? boundingBox.getSize()
            : {
                x: boundingBox.max.x - boundingBox.min.x,
                y: boundingBox.max.y - boundingBox.min.y,
                z: boundingBox.max.z - boundingBox.min.z,
              };

          const distance = Math.max(size.x, size.y, size.z) * 2;

          // Set camera position
          const newPosition = { x: center.x, y: center.y, z: center.z };

          switch (view) {
            case 'left':
              newPosition.x = center.x - distance;
              break;
            case 'right':
              newPosition.x = center.x + distance;
              break;
            case 'front':
              newPosition.y = center.y + distance;
              break;
            case 'back':
              newPosition.y = center.y - distance;
              break;
            case 'top':
              newPosition.z = center.z + distance;
              break;
            case 'bottom':
              newPosition.z = center.z - distance;
              break;
          }

          if (camera.position && camera.position.set) {
            camera.position.set(newPosition.x, newPosition.y, newPosition.z);
          } else {
            camera.position.x = newPosition.x;
            camera.position.y = newPosition.y;
            camera.position.z = newPosition.z;
          }

          if (camera.lookAt) {
            camera.lookAt(center);
          }

          // Trigger redraw
          if (viewer.setNeedsRedraw) {
            viewer.setNeedsRedraw();
          } else if (viewer.render) {
            viewer.render();
          }

          debugLog('Camera view set to:', view);
        } catch (error) {
          debugError('Error setting camera view:', error);
        }
      },

      setNavigationMode(mode) {
        const viewer = this.getViewer();
        if (!viewer) {
          debugWarn('Viewer not available for navigation mode:', mode);
          return;
        }

        if (!this.isViewerReady()) {
          debugWarn('Viewer not ready for navigation mode');
          return;
        }

        try {
          if (!viewer.setControls) {
            debugWarn('setControls method not available');
            return;
          }

          let controlsCreated = false;

          switch (mode) {
            case 'earth':
              controlsCreated = this.createControls(viewer, ['EarthControls', 'GeoControls']);
              break;
            case 'flight':
              controlsCreated = this.createControls(viewer, ['FirstPersonControls', 'FlyControls']);
              break;
            case 'helicopter':
              controlsCreated = this.createControls(viewer, ['GeoControls', 'EarthControls']);
              break;
            case 'orbit':
            default:
              controlsCreated = this.createControls(viewer, ['OrbitControls', 'TrackballControls']);
              break;
          }

          if (controlsCreated) {
            debugLog('Navigation mode set to:', mode);
          } else {
            debugWarn('Could not create navigation controls:', mode);
          }
        } catch (error) {
          debugError('Error setting navigation mode:', error);
        }
      },

      createControls(viewer, controlNames) {
        if (!window.Potree || !viewer.setControls) return false;

        for (const controlName of controlNames) {
          if (window.Potree[controlName]) {
            try {
              const controls = new window.Potree[controlName](viewer);
              viewer.setControls(controls);
              return true;
            } catch (error) {
              debugWarn(`Failed to create ${controlName}:`, error);
            }
          }
        }
        return false;
      },

      updateBackground(value) {
        try {
          if (this.$refs.potreeViewer) {
            this.$refs.potreeViewer.setBackground(value);
            this.$emit('background-changed', value);
            debugLog('Background updated to:', value);
          }
        } catch (error) {
          debugError('Error updating background:', error);
        }
      },

      updatePointBudget() {
        try {
          const viewer = this.getViewer();
          if (viewer && viewer.setPointBudget) {
            viewer.setPointBudget(this.currentPointBudget);
            this.$emit('point-budget-changed', this.currentPointBudget);
            debugLog('Point budget updated to:', this.currentPointBudget);
          }
        } catch (error) {
          debugError('Error updating point budget:', error);
        }
      },

      updateFOV() {
        try {
          const viewer = this.getViewer();
          if (viewer && viewer.setFOV) {
            viewer.setFOV(this.currentFOV);
            this.$emit('fov-changed', this.currentFOV);
            debugLog('FOV updated to:', this.currentFOV);
          }
        } catch (error) {
          debugError('Error updating FOV:', error);
        }
      },

      exportGeoJSON() {
        try {
          const viewer = this.getViewer();
          if (!viewer) {
            debugWarn('Viewer not available for GeoJSON export');
            return;
          }

          let measurements = [];

          // Try different ways to access measurements
          if (viewer.scene && viewer.scene.measurements) {
            measurements = viewer.scene.measurements;
          } else if (viewer.measurements) {
            measurements = viewer.measurements;
          } else if (viewer.scene && viewer.scene.annotations) {
            measurements = viewer.scene.annotations;
          }

          if (measurements.length === 0) {
            debugWarn('No measurements found to export');
            return;
          }

          const features = measurements.map((measurement) => {
            const feature = {
              type: 'Feature',
              properties: {
                type: measurement.constructor?.name || 'Measurement',
              },
              geometry: {
                type: 'LineString',
                coordinates: [],
              },
            };

            // Add measurements properties
            if (measurement.getTotalDistance) {
              feature.properties.distance = measurement.getTotalDistance();
            }
            if (measurement.getArea) {
              feature.properties.area = measurement.getArea();
            }

            // Add coordinates
            if (measurement.points && Array.isArray(measurement.points)) {
              feature.geometry.coordinates = measurement.points.map((p) => [
                p.x || 0,
                p.y || 0,
                p.z || 0,
              ]);
            }

            return feature;
          });

          const geojson = {
            type: 'FeatureCollection',
            features: features,
          };

          // Download the file
          const blob = new Blob([JSON.stringify(geojson, null, 2)], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'potree_measurements.geojson';
          a.click();
          URL.revokeObjectURL(url);

          debugLog('GeoJSON exported with', features.length, 'measurements');
        } catch (error) {
          debugError('Error exporting GeoJSON:', error);
        }
      },

      exportDXF() {
        try {
          const viewer = this.getViewer();
          if (viewer && viewer.exportDXF) {
            viewer.exportDXF();
            debugLog('DXF export initiated');
          } else {
            debugWarn('DXF export not available');
          }
        } catch (error) {
          debugError('Error exporting DXF:', error);
        }
      },

      handleMaterialChange(materialSettings) {
        debugLog('Material settings applied:', materialSettings);
        this.$emit('material-changed', materialSettings);
      },

      handleFilterChange(filterSettings) {
        debugLog('Filter changed:', filterSettings);
        this.$emit('filter-changed', filterSettings);
      },

      handleFiltersApplied(filterSettings) {
        debugLog('Filters applied:', filterSettings);
        this.$emit('filters-applied', filterSettings);
      },

      handleFiltersCleared() {
        debugLog('All filters cleared');
        this.$emit('filters-cleared');
      },

      // ClippingToolbar event handlers
      handleVolumeCreated({ type, volume }) {
        debugLog('[PotreeMeasurementsApp] Volume created from toolbar:', { type, volume });
        this.$emit('volume-created', { type, volume });
      },

      handleVolumesCleared() {
        debugLog('[PotreeMeasurementsApp] All volumes cleared from toolbar');
        this.$emit('volumes-cleared');
      },

      handleClipTaskChanged({ taskType, clipTaskValue }) {
        debugLog('[PotreeMeasurementsApp] Clip task changed:', { taskType, clipTaskValue });
        this.$emit('clip-task-changed', { taskType, clipTaskValue });
      },

      formatBounds(pointcloud) {
        if (pointcloud && pointcloud.boundingBox) {
          const bb = pointcloud.boundingBox;
          return `${bb.min.x.toFixed(2)}, ${bb.min.y.toFixed(2)}, ${bb.min.z.toFixed(
            2,
          )} to ${bb.max.x.toFixed(2)}, ${bb.max.y.toFixed(2)}, ${bb.max.z.toFixed(2)}`;
        }
        return 'Unknown';
      },

      handleIconError(tool) {
        debugWarn('Failed to load icon for tool:', tool.value, 'using fallback');
        // Set fallbackIcon to true to use v-icon instead of img
        this.$set(tool, 'useFallback', true);
      },

      // Public API Methods
      getViewer() {
        return (
          this.viewerInstance ||
          (this.$refs.potreeViewer ? this.$refs.potreeViewer.getViewer() : null)
        );
      },

      getPointCloud() {
        return this.pointCloudInstance || this.pointCloudInfo;
      },

      isLoaded() {
        return this.pointCloudLoaded;
      },

      isViewerReady() {
        return this.$refs.potreeViewer ? this.$refs.potreeViewer.isViewerReady() : false;
      },

      isPointCloudReady() {
        return this.$refs.potreeViewer ? this.$refs.potreeViewer.isPointCloudReady() : false;
      },

      // Cylinder Fitting Methods
      activateCylinderMode() {
        logEvent('Cylinder Mode Activated', {
          circles: 0,
          active: true,
        });

        this.cylinderMode.active = true;
        this.cylinderMode.circles = [];
        this.cylinderMode.currentStep = 'circle1'; // NEW: Auto-start Circle 1
        this.cylinderMode.circle1Complete = false; // NEW: Reset completion flags
        this.cylinderMode.circle2Complete = false; // NEW: Reset completion flags
        this.showCylinderInstructions = true;

        // Add keyboard listener for Enter and Escape keys
        this.cylinderMode.keyboardListener = (e) => {
          if (e.key === 'Enter') {
            this.finalizeCylinder();
          } else if (e.key === 'Escape') {
            this.cancelCylinderMode();
          }
        };
        document.addEventListener('keydown', this.cylinderMode.keyboardListener);

        // Auto-activate first circle tool
        this.activateCircleTool();
      },

      deactivateCylinderMode() {
        this.cylinderMode.active = false;
        this.showCylinderInstructions = false;

        // Remove keyboard listener
        if (this.cylinderMode.keyboardListener) {
          document.removeEventListener('keydown', this.cylinderMode.keyboardListener);
          this.cylinderMode.keyboardListener = null;
        }

        // Clean up preview if exists
        if (this.cylinderMode.previewCylinder) {
          const viewer = this.getViewer();
          if (viewer && viewer.scene && viewer.scene.scene) {
            viewer.scene.scene.remove(this.cylinderMode.previewCylinder);
          }
          this.cylinderMode.previewCylinder = null;
        }

        // Reset all state properties
        this.cylinderMode.circles = [];
        this.cylinderMode.circle1Measurement = null; // Reset Circle 1 reference
        this.cylinderMode.circle2Measurement = null; // Reset Circle 2 reference
        this.cylinderMode.currentStep = null; // Reset step tracker
        this.cylinderMode.circle1Complete = false; // Reset completion flags
        this.cylinderMode.circle2Complete = false; // Reset completion flags
      },

      cancelCylinderMode() {
        const viewer = this.getViewer();

        // First, remove from activeMeasurements array (so they disappear from sidebar list)
        if (this.cylinderMode.circle1Measurement) {
          const circle1Index = this.activeMeasurements.findIndex(
            m => m.measurement === this.cylinderMode.circle1Measurement
          );
          if (circle1Index > -1) {
            this.activeMeasurements.splice(circle1Index, 1);
          }
        }

        if (this.cylinderMode.circle2Measurement) {
          const circle2Index = this.activeMeasurements.findIndex(
            m => m.measurement === this.cylinderMode.circle2Measurement
          );
          if (circle2Index > -1) {
            this.activeMeasurements.splice(circle2Index, 1);
          }
        }

        // Then remove from 3D scene
        if (viewer && viewer.scene) {
          if (this.cylinderMode.circle1Measurement) {
            try {
              viewer.scene.removeMeasurement(this.cylinderMode.circle1Measurement);
            } catch (error) {
              debugWarn('Error removing circle 1:', error);
            }
          }

          if (this.cylinderMode.circle2Measurement) {
            try {
              viewer.scene.removeMeasurement(this.cylinderMode.circle2Measurement);
            } catch (error) {
              debugWarn('Error removing circle 2:', error);
            }
          }

          // Also remove from circles array (legacy fallback)
          this.cylinderMode.circles.forEach((circle) => {
            try {
              viewer.scene.removeMeasurement(circle);
            } catch (error) {
              debugWarn('Error removing circle during cancel:', error);
            }
          });
        }

        this.deactivateCylinderMode();
        this.$emit('cylinder-mode-cancelled');
      },

      // New methods for explicit circle control
      startCircle1() {
        const viewer = this.getViewer();

        // Check if Circle 1 already exists - if so, remove it for override
        if (this.cylinderMode.circle1Measurement) {
          // Remove from scene
          if (viewer && viewer.scene) {
            try {
              viewer.scene.removeMeasurement(this.cylinderMode.circle1Measurement);
            } catch (error) {
              debugWarn('Error removing Circle 1 from scene:', error);
            }
          }

          // Remove from circles array
          const index = this.cylinderMode.circles.indexOf(this.cylinderMode.circle1Measurement);
          if (index > -1) {
            this.cylinderMode.circles.splice(index, 1);
          }

          // Remove from activeMeasurements
          const measurementIndex = this.activeMeasurements.findIndex(
            m => m.measurement === this.cylinderMode.circle1Measurement
          );
          if (measurementIndex > -1) {
            this.activeMeasurements.splice(measurementIndex, 1);
          }

          // Remove preview cylinder if it exists
          if (this.cylinderMode.previewCylinder) {
            if (viewer && viewer.scene && viewer.scene.scene) {
              viewer.scene.scene.remove(this.cylinderMode.previewCylinder);
            }
            this.cylinderMode.previewCylinder = null;
          }

          // Reset Circle 1 state
          this.cylinderMode.circle1Measurement = null;
          this.cylinderMode.circle1Complete = false;
        }

        this.cylinderMode.currentStep = 'circle1';
        this.activateCircleTool();
      },

      startCircle2() {
        if (this.circle1Status !== 'complete') {
          debugWarn('Circle 1 must be complete before starting Circle 2');
          return;
        }

        const viewer = this.getViewer();

        // Check if Circle 2 already exists - if so, remove it for override
        if (this.cylinderMode.circle2Measurement) {
          // Remove from scene
          if (viewer && viewer.scene) {
            try {
              viewer.scene.removeMeasurement(this.cylinderMode.circle2Measurement);
            } catch (error) {
              debugWarn('Error removing Circle 2 from scene:', error);
            }
          }

          // Remove from circles array
          const index = this.cylinderMode.circles.indexOf(this.cylinderMode.circle2Measurement);
          if (index > -1) {
            this.cylinderMode.circles.splice(index, 1);
          }

          // Remove from activeMeasurements
          const measurementIndex = this.activeMeasurements.findIndex(
            m => m.measurement === this.cylinderMode.circle2Measurement
          );
          if (measurementIndex > -1) {
            this.activeMeasurements.splice(measurementIndex, 1);
          }

          // Remove preview cylinder if it exists
          if (this.cylinderMode.previewCylinder) {
            if (viewer && viewer.scene && viewer.scene.scene) {
              viewer.scene.scene.remove(this.cylinderMode.previewCylinder);
            }
            this.cylinderMode.previewCylinder = null;
          }

          // Reset Circle 2 state
          this.cylinderMode.circle2Measurement = null;
          this.cylinderMode.circle2Complete = false;
        }

        this.cylinderMode.currentStep = 'circle2';
        this.activateCircleTool();
      },

      isToolDisabledInCylinderMode(toolValue) {
        // Disable circle button when in cylinder mode
        if (this.cylinderMode.active && toolValue === 'circle') {
          return true;
        }
        // Optionally disable all other tools except fit-cylinder during cylinder mode
        if (this.cylinderMode.active && toolValue !== 'fit-cylinder') {
          return true;
        }
        return false;
      },

      activateCircleTool() {
        const viewer = this.getViewer();
        if (!viewer || !viewer.measuringTool) {
          debugWarn('Viewer or measuring tool not available');
          return;
        }

        try {
          // Clear any existing tool state
          if (viewer.setTool) {
            viewer.setTool(null);
          }

          // Start circle insertion
          const measurement = viewer.measuringTool.startInsertion({
            showDistances: false,
            showHeight: false,
            showArea: false,
            showCircle: true,
            showEdges: false,
            closed: false,
            maxMarkers: 3,
            name: `Circle ${this.cylinderMode.currentStep === 'circle1' ? '1' : '2'}`,
          });

          if (!measurement) {
            debugError('Failed to create measurement');
            return;
          }

          // Set up marker_dropped event listener to detect completion
          // Using marker_dropped instead of marker_added ensures circle geometry is fully rendered before marking complete
          const markerDroppedHandler = (event) => {
            const pointCount = measurement.points ? measurement.points.length : 0;

            // Check if circle is complete (3 points for a circle)
            if (pointCount === 3) {
              // Mark step as complete and store reference
              if (this.cylinderMode.currentStep === 'circle1') {
                this.cylinderMode.circle1Complete = true;
                this.cylinderMode.circle1Measurement = measurement;
                this.cylinderMode.currentStep = null;
              } else if (this.cylinderMode.currentStep === 'circle2') {
                this.cylinderMode.circle2Complete = true;
                this.cylinderMode.circle2Measurement = measurement;
                this.cylinderMode.currentStep = null;
              }

              // Update preview if both circles are complete
              if (this.cylinderMode.circle1Complete && this.cylinderMode.circle2Complete) {
                this.updateCylinderPreview();
              }

              // Remove this event listener after completion
              measurement.removeEventListener('marker_dropped', markerDroppedHandler);
            }
          };

          // Attach marker_dropped event listener (fires AFTER geometry is fully rendered)
          measurement.addEventListener('marker_dropped', markerDroppedHandler);

          // Add measurement to tracking (for backward compatibility and display)
          this.cylinderMode.circles.push(measurement);
          this.addMeasurement('circle', measurement);
        } catch (error) {
          debugError('Error activating circle tool:', error);
        }
      },

      // Cylinder Calculation Methods
      extractCircleData(circleMeasurement) {
        try {
          const points = circleMeasurement.points || circleMeasurement.markers;
          if (!points || points.length < 3) {
            debugWarn('Circle does not have enough points');
            return null;
          }

          // Use existing circle calculation methods
          const center = this.calculateCircumcenter(points[0], points[1], points[2]);
          if (!center) {
            debugWarn('Could not calculate circle center');
            return null;
          }

          const radius = this.calculateCircleRadius(center, points[0]);
          if (!radius || radius === 0) {
            debugWarn('Could not calculate circle radius');
            return null;
          }

          return {
            center,
            radius,
            points: points.map((p) => p.position || p),
            measurement: circleMeasurement,
          };
        } catch (error) {
          debugError('Error extracting circle data:', error);
          return null;
        }
      },

      calculateCylinderFromCircles(circles) {
        if (!circles || circles.length < 2) {
          debugWarn('Need at least 2 circles to calculate cylinder');
          return null;
        }

        try {
          // Extract circle data
          const circle1 = this.extractCircleData(circles[0]);
          const circle2 = this.extractCircleData(circles[1]);

          if (!circle1 || !circle2) {
            debugWarn('Could not extract circle data');
            return null;
          }

          // Cylinder axis: vector from center1 to center2
          const axis = {
            x: circle2.center.x - circle1.center.x,
            y: circle2.center.y - circle1.center.y,
            z: circle2.center.z - circle1.center.z,
          };

          // Cylinder height: distance between centers
          const height = Math.sqrt(axis.x * axis.x + axis.y * axis.y + axis.z * axis.z);

          if (height === 0) {
            debugWarn('Circle centers are at the same position');
            return null;
          }

          // Cylinder position: midpoint between centers
          const position = {
            x: (circle1.center.x + circle2.center.x) / 2,
            y: (circle1.center.y + circle2.center.y) / 2,
            z: (circle1.center.z + circle2.center.z) / 2,
          };

          // Average radius
          const radius = (circle1.radius + circle2.radius) / 2;

          // Calculate rotation to align cylinder with axis
          const rotation = this.calculateCylinderRotation(axis, height);

          return {
            position,
            axis,
            radius,
            height,
            rotation,
            circles: [circle1, circle2],
          };
        } catch (error) {
          debugError('Error calculating cylinder from circles:', error);
          return null;
        }
      },

      calculateCylinderRotation(axis, height) {
        if (!window.THREE) {
          debugError('THREE.js not available');
          return null;
        }

        try {
          // THREE.js CylinderGeometry is oriented along Y-axis by default
          // Need to rotate to align with our axis vector
          const axisVector = new THREE.Vector3(axis.x, axis.y, axis.z);
          axisVector.normalize();

          const yAxis = new THREE.Vector3(0, 1, 0);

          // Calculate quaternion rotation from Y to axis
          const quaternion = new THREE.Quaternion();
          quaternion.setFromUnitVectors(yAxis, axisVector);

          return quaternion;
        } catch (error) {
          debugError('Error calculating cylinder rotation:', error);
          return null;
        }
      },

      normalizeVector(vector) {
        const magnitude = Math.sqrt(
          vector.x * vector.x + vector.y * vector.y + vector.z * vector.z,
        );

        if (magnitude === 0) return { x: 0, y: 0, z: 0 };

        return {
          x: vector.x / magnitude,
          y: vector.y / magnitude,
          z: vector.z / magnitude,
        };
      },

      // THREE.js Cylinder Rendering Methods using Potree's internal THREE.js r124
      createCylinderMesh(cylinderData, opacity = 0.2) {
        if (!cylinderData) {
          debugWarn('No cylinder data provided');
          return null;
        }

        // Use Potree's built-in helper function that uses THREE.js r124
        if (!window.Potree || !window.Potree.createCylinderMesh) {
          debugError('Potree.createCylinderMesh not available');
          return null;
        }

        try {
          const mesh = window.Potree.createCylinderMesh(cylinderData, opacity);
          return mesh;
        } catch (error) {
          debugError('Error creating cylinder mesh:', error);
          return null;
        }
      },

      updateCylinderPreview() {
        // Use specific circle references instead of array
        if (!this.cylinderMode.circle1Measurement || !this.cylinderMode.circle2Measurement) {
          return;
        }

        const viewer = this.getViewer();
        if (!viewer || !viewer.scene || !viewer.scene.scene) {
          debugWarn('Viewer not available for preview');
          return;
        }

        try {
          // Remove old preview
          if (this.cylinderMode.previewCylinder) {
            viewer.scene.scene.remove(this.cylinderMode.previewCylinder);
            this.cylinderMode.previewCylinder = null;
          }

          // Calculate cylinder from specific circle measurements
          const circles = [this.cylinderMode.circle1Measurement, this.cylinderMode.circle2Measurement];
          const cylinderData = this.calculateCylinderFromCircles(circles);
          if (!cylinderData) {
            debugWarn('Could not calculate cylinder data');
            return;
          }

          // Create preview cylinder with 5% opacity (lighter preview)
          const previewMesh = this.createCylinderMesh(cylinderData, 0.05);
          if (!previewMesh) {
            debugWarn('Could not create preview mesh');
            return;
          }

          this.cylinderMode.previewCylinder = previewMesh;
          viewer.scene.scene.add(previewMesh);
        } catch (error) {
          debugError('Error updating cylinder preview:', error);
        }
      },

      finalizeCylinder() {
        const cylinderTimer = startTimer('Cylinder Finalization');

        // Use specific circle references
        const hasCircle1 = !!this.cylinderMode.circle1Measurement;
        const hasCircle2 = !!this.cylinderMode.circle2Measurement;

        logEvent('Finalizing Cylinder', {
          hasCircle1,
          hasCircle2,
          canFinalize: hasCircle1 && hasCircle2,
        });

        if (!hasCircle1 || !hasCircle2) {
          debugWarn('Need both Circle 1 and Circle 2 to create cylinder');
          alert('Please create both circles before finalizing the cylinder');
          return;
        }

        const viewer = this.getViewer();
        if (!viewer || !viewer.scene || !viewer.scene.scene) {
          debugWarn('Viewer not available');
          return;
        }

        try {
          // Calculate final cylinder using specific circle references
          const circles = [this.cylinderMode.circle1Measurement, this.cylinderMode.circle2Measurement];
          const cylinderData = this.calculateCylinderFromCircles(circles);
          if (!cylinderData) {
            debugError('Could not calculate cylinder data');
            alert('Error calculating cylinder. Please try again.');
            return;
          }

          // Remove preview (5% opacity)
          if (this.cylinderMode.previewCylinder) {
            viewer.scene.scene.remove(this.cylinderMode.previewCylinder);
            this.cylinderMode.previewCylinder = null;
          }

          // Create final cylinder (10% opacity - blue)
          const finalCylinder = this.createCylinderMesh(cylinderData, 0.1);
          if (!finalCylinder) {
            debugError('Could not create final cylinder mesh');
            alert('Error creating cylinder mesh. Please try again.');
            return;
          }

          viewer.scene.scene.add(finalCylinder);

          // Store as custom measurement with specific circle references
          const cylinderMeasurement = {
            id: `cylinder-${Date.now()}`,
            type: 'fitted-cylinder',
            cylinderData,
            mesh: finalCylinder,
            circles: [this.cylinderMode.circle1Measurement, this.cylinderMode.circle2Measurement],
            timestamp: new Date(),
            displayName: `Cylinder ${
              this.activeMeasurements.filter((m) => m.type === 'fitted-cylinder').length + 1
            }`,
            icon: 'mdi-cylinder',
            expanded: false,
          };

          this.activeMeasurements.push(cylinderMeasurement);

          // Remove Circle 1 and Circle 2 from scene and measurements panel
          // Remove Circle 1
          if (this.cylinderMode.circle1Measurement) {
            try {
              viewer.scene.removeMeasurement(this.cylinderMode.circle1Measurement);

              // Remove from activeMeasurements
              const circle1Index = this.activeMeasurements.findIndex(
                m => m.measurement === this.cylinderMode.circle1Measurement
              );
              if (circle1Index > -1) {
                this.activeMeasurements.splice(circle1Index, 1);
              }
            } catch (error) {
              debugWarn('Error removing Circle 1 after cylinder creation:', error);
            }
          }

          // Remove Circle 2
          if (this.cylinderMode.circle2Measurement) {
            try {
              viewer.scene.removeMeasurement(this.cylinderMode.circle2Measurement);

              // Remove from activeMeasurements
              const circle2Index = this.activeMeasurements.findIndex(
                m => m.measurement === this.cylinderMode.circle2Measurement
              );
              if (circle2Index > -1) {
                this.activeMeasurements.splice(circle2Index, 1);
              }
            } catch (error) {
              debugWarn('Error removing Circle 2 after cylinder creation:', error);
            }
          }

          // Set up circle edit listeners
          this.setupCylinderEditListeners(cylinderMeasurement);

          logEvent('Cylinder Created', {
            radius: cylinderData.radius.toFixed(2),
            height: cylinderData.height.toFixed(2),
            hasCircle1,
            hasCircle2,
          });

          cylinderTimer.end('Cylinder Finalized');

          // Exit cylinder mode
          this.deactivateCylinderMode();

          this.$emit('cylinder-created', cylinderMeasurement);
        } catch (error) {
          debugError('Error finalizing cylinder:', error);
          alert('Error finalizing cylinder. Please try again.');
        }
      },

      setupCylinderEditListeners(cylinderMeasurement) {
        const updateCylinder = () => {
          try {
            const viewer = this.getViewer();
            if (!viewer || !viewer.scene || !viewer.scene.scene) return;

            // Recalculate cylinder from updated circles
            const newCylinderData = this.calculateCylinderFromCircles(cylinderMeasurement.circles);

            if (!newCylinderData) return;

            // Remove old mesh
            viewer.scene.scene.remove(cylinderMeasurement.mesh);

            // Create updated mesh with same opacity (0.2)
            const newMesh = this.createCylinderMesh(newCylinderData, 0.1);
            if (!newMesh) return;

            viewer.scene.scene.add(newMesh);

            // Update stored data
            cylinderMeasurement.cylinderData = newCylinderData;
            cylinderMeasurement.mesh = newMesh;
          } catch (error) {
            debugError('Error updating cylinder:', error);
          }
        };

        // Listen to all circles
        cylinderMeasurement.circles.forEach((circle) => {
          const events = ['marker_moved', 'marker_dropped', 'measurement_changed', 'change'];

          events.forEach((evt) => {
            if (circle.addEventListener) {
              try {
                circle.addEventListener(evt, updateCylinder);
              } catch (error) {
                debugWarn(`Could not add listener for ${evt}:`, error);
              }
            }
          });
        });
      },

      // ========================================
      // Lasso Selection Methods
      // ========================================

      /**
       * Activate lasso selection tool
       * Locks camera and enables polygon drawing
       */
      async activateLassoTool() {
        const viewer = this.getViewer();
        if (!viewer) {
          debugError('[ScreenLasso] Viewer not available');
          return;
        }

        // Lock camera
        const controls = viewer.getControls();
        if (controls) {
          controls.enabled = false;
          this.cameraLocked = true;
        }

        // Capture camera state
        const camera = viewer.scene.getActiveCamera();
        const { captureCameraState } = require('../utils/lassoGeometry');
        this.lassoTool.cameraState = captureCameraState(camera);

        // Reset tool state
        this.lassoTool.active = true;
        this.lassoTool.locked = true;
        this.lassoTool.points = [];
        this.lassoTool.hoverPos = null;
        this.lassoTool.instruction = 'Click to add points. Double-click, press Enter, or right-click to finish.';

        // Add keyboard listener
        document.addEventListener('keydown', this.handleLassoKeyboard);

        debugLog('[ScreenLasso] Tool activated');
        logEvent('Lasso Tool Activated', {
          cameraLocked: this.cameraLocked,
          cameraPosition: camera.position.toArray()
        });
      },

      /**
       * Deactivate lasso tool and unlock camera
       */
      deactivateLassoTool() {
        const viewer = this.getViewer();
        if (viewer && this.cameraLocked) {
          const controls = viewer.getControls();
          if (controls) {
            controls.enabled = true;
            this.cameraLocked = false;
          }
        }

        this.lassoTool.active = false;
        this.lassoTool.locked = false;
        this.lassoTool.points = [];
        this.lassoTool.hoverPos = null;

        // Remove keyboard listener
        document.removeEventListener('keydown', this.handleLassoKeyboard);

        logEvent('Lasso Tool Deactivated', {
          historyCount: this.lassoHistory.length
        });
      },

      /**
       * Handle click to add polygon vertex
       */
      handleLassoClick(event) {
        if (!this.lassoTool.active) return;

        // Use clientX/clientY for proper coordinate conversion with screenToNDC
        const point = {
          x: event.clientX,
          y: event.clientY
        };

        this.lassoTool.points.push(point);

        const vertexCount = this.lassoTool.points.length;

        // Update instruction
        if (vertexCount < 3) {
          this.lassoTool.instruction = `Click to add points (${vertexCount}/3 minimum)`;
        } else {
          this.lassoTool.instruction = 'Double-click, press Enter, or right-click to finish';
        }
      },

      /**
       * Handle mouse move for preview line
       */
      handleLassoHover(event) {
        if (!this.lassoTool.active) return;

        // Use clientX/clientY for consistency with click handling
        this.lassoTool.hoverPos = {
          x: event.clientX,
          y: event.clientY
        };
      },

      /**
       * Complete lasso and apply filter
       */
      async handleLassoComplete() {
        if (!this.lassoTool.active) {
          debugWarn('[ScreenLasso] ⚠️  Cannot complete - tool not active');
          return;
        }

        if (this.lassoTool.points.length < 3) {
          debugWarn('[ScreenLasso] ⚠️  Need at least 3 points to complete lasso', {
            currentPoints: this.lassoTool.points.length,
            needed: 3
          });
          return;
        }

        const lassoTimer = startTimer('Lasso Completion');

        try {
          // Apply filter using GPU shaders (multi-lasso support)
          const { addLassoToShaderFilter, setCumulativeMode } = require('../utils/lassoShaderGPU');
          const viewer = this.getViewer();

          if (!viewer) {
            throw new Error('Viewer not available');
          }

          // Get canvas dimensions for NDC conversion
          const canvas = viewer.renderer.domElement;
          const rect = canvas.getBoundingClientRect();

          // Convert polygon from screen pixels to NDC coordinates
          // This makes the lasso viewport-independent
          const polygonNDC = this.lassoTool.points.map(p => ({
            x: ((p.x - rect.left) / rect.width) * 2 - 1,
            y: -(((p.y - rect.top) / rect.height) * 2 - 1)
          }));

          // Determine lasso type and filter mode
          const lassoType = this.isCleanLassoMode ? 'clean' : 'filter';
          const filterMode = lassoType === 'clean' ? 'outside' : 'inside';

          // Create lasso operation with NDC coordinates
          const lassoOp = {
            id: 'lasso_' + Date.now(),
            timestamp: new Date().toISOString(),
            type: lassoType,  // 'filter' | 'clean'
            polygon: polygonNDC,  // NDC coordinates (-1 to 1)
            format: 'ndc',  // Explicit format flag for backward compatibility
            cameraState: this.lassoTool.cameraState,
            filterMode: filterMode,  // 'inside' for filter, 'outside' for clean
            active: true,
            visible: true,
            pointsFiltered: 0,
            originalViewport: { width: rect.width, height: rect.height }  // For debugging
          };

          // Reset clean lasso mode after creating the lasso
          this.isCleanLassoMode = false;

          // Set cumulative mode before adding lasso
          setCumulativeMode(this.lassoCumulativeMode);

          // Apply filter - pass format as 'ndc' since polygon is now in NDC coordinates
          const result = addLassoToShaderFilter(
            viewer,
            lassoOp.polygon,
            lassoOp.filterMode,
            lassoOp.id,
            null,  // savedCameraState not needed for new lassos
            'ndc',  // Format: polygon is in NDC coordinates
            lassoType  // Lasso type: 'filter' or 'clean'
          );

          if (!result.success) {
            throw new Error(result.error || 'Failed to apply GPU filter');
          }

          // Track active lasso
          this.activeLassoIds.push(lassoOp.id);

          // Add to history (saved with AVA tag, not LocalStorage)
          this.lassoHistory.push(lassoOp);
          this.lassoHistoryIndex = this.lassoHistory.length - 1;
          this.addLassoToPanel(lassoOp);

          // Render debug visualization with color based on lasso index
          this.renderLassoDebugMesh(lassoOp, this.lassoHistory.length - 1);

          // Auto-deactivate tool and open panel
          this.deactivateLassoTool();
          this.showMeasurementsPanel = true;

          lassoTimer.end('Lasso Complete');
          debugLog('[ScreenLasso] ✅ Completed:', lassoOp.id, 'vertices:', lassoOp.polygon.length);

          this.$emit('lasso-completed', lassoOp);

          logEvent('Lasso Selection Complete', {
            vertices: lassoOp.polygon.length,
            mode: lassoOp.filterMode,
            pointsFiltered: lassoOp.pointsFiltered,
            historyCount: this.lassoHistory.length,
            activeLassos: this.activeLassoIds.length,
            cumulativeMode: this.lassoCumulativeMode
          });

          // Recalculate corrosion statistics for lasso selection
          if (this.$refs.potreeViewer && this.$refs.potreeViewer.recalculateCorrosionForLassoSelection) {
            this.$refs.potreeViewer.recalculateCorrosionForLassoSelection();
          }

        } catch (error) {
          debugError('[ScreenLasso] ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
          debugError('[ScreenLasso] ❌ ERROR DURING LASSO COMPLETION');
          debugError('[ScreenLasso] ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
          debugError('[ScreenLasso] Error details:', {
            message: error.message,
            name: error.name,
            vertices: this.lassoTool.points.length,
            mode: this.lassoTool.mode
          });
          debugError('[ScreenLasso] Stack trace:', error.stack);
          debugError('[ScreenLasso] ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
          alert('Error applying lasso filter. See console for details.');
        }
      },

      /**
       * Cancel current lasso without applying
       */
      cancelLasso() {
        // Capture state BEFORE clearing (fix for logging bug)
        const hadPoints = this.lassoTool.points.length > 0;
        const vertexCount = this.lassoTool.points.length;

        debugLog('[ScreenLasso] 🚫 Cancelling lasso', {
          hadPoints: hadPoints,
          vertexCount: vertexCount,
          historyCount: this.lassoHistory.length
        });

        // Clear current lasso
        this.lassoTool.points = [];
        this.lassoTool.hoverPos = null;

        if (this.lassoHistory.length === 0) {
          // No lassos yet, deactivate completely
          debugLog('[ScreenLasso] 🔓 No lassos in history - deactivating tool');
          this.deactivateLassoTool();
        } else {
          // Keep tool active for more lassos
          debugLog('[ScreenLasso] 🔄 Tool still active - ready for next lasso');
          this.lassoTool.instruction = 'Lasso cancelled. Click to start new lasso, or press Escape to unlock camera.';
        }

        debugLog('[ScreenLasso] ✅ Lasso cancelled');

        logEvent('Lasso Cancelled', {
          hadPoints: hadPoints,
          vertexCount: vertexCount,
          historyCount: this.lassoHistory.length
        });
      },

      /**
       * Toggle filter mode inside/outside
       */
      toggleLassoMode() {
        this.lassoTool.mode = this.lassoTool.mode === 'inside' ? 'outside' : 'inside';
        debugLog('[ScreenLasso] Filter mode changed to:', this.lassoTool.mode);

        logEvent('Lasso Mode Toggled', {
          newMode: this.lassoTool.mode
        });
      },

      /**
       * Toggle inside/outside mode for existing lasso in panel
       * Updates the filter mode and immediately re-applies the GPU shader filter
       */
      toggleLassoFilterMode(measurementItem) {
        if (measurementItem.type !== 'screen-lasso' || !measurementItem.lassoData) {
          debugWarn('[ScreenLasso] ⚠️  Cannot toggle mode - not a lasso item');
          return;
        }

        const lassoData = measurementItem.lassoData;
        const oldMode = lassoData.filterMode;
        const newMode = oldMode === 'inside' ? 'outside' : 'inside';

        debugLog('[ScreenLasso] 🔄 Toggling filter mode in panel:', {
          lassoId: lassoData.id,
          oldMode,
          newMode
        });

        // Update lasso data
        lassoData.filterMode = newMode;

        // Update history
        const historyItem = this.lassoHistory.find(l => l.id === lassoData.id);
        if (historyItem) {
          historyItem.filterMode = newMode;
          debugLog('[ScreenLasso] ✅ History updated');
        }

        // Re-apply GPU filter with new mode
        try {
          const { applyLassoShaderFilter } = require('../utils/lassoShaderGPU');
          const viewer = this.getViewer();

          if (!viewer) {
            throw new Error('Viewer not available');
          }

          debugLog('[ScreenLasso] 🚀 Re-applying GPU filter with new mode...');
          const result = applyLassoShaderFilter(
            viewer,
            lassoData.polygon,
            newMode,
            lassoData.id
          );

          if (result.success) {
            debugLog('[ScreenLasso] ✅ Filter mode toggled successfully:', {
              lassoId: lassoData.id,
              newMode,
              materialsUpdated: result.materialsUpdated
            });

            // Force UI update to reflect new mode
            this.$forceUpdate();
          } else {
            throw new Error(result.error || 'Failed to apply GPU filter');
          }

          logEvent('Lasso Filter Mode Toggled', {
            lassoId: lassoData.id,
            oldMode,
            newMode
          });

        } catch (error) {
          debugError('[ScreenLasso] ❌ Failed to toggle filter mode:', {
            error: error.message,
            lassoId: lassoData.id
          });

          // Revert mode change on error
          lassoData.filterMode = oldMode;
          if (historyItem) {
            historyItem.filterMode = oldMode;
          }

          alert('Error toggling lasso mode. See console for details.');
        }
      },

      /**
       * Handle keyboard shortcuts for lasso tool
       */
      handleLassoKeyboard(event) {
        if (!this.lassoTool.active) {
          debugLog('[ScreenLasso] ⚠️  Keyboard event ignored - tool not active');
          return;
        }

        const key = event.key.toLowerCase();
        debugLog('[ScreenLasso] ⌨️  Keyboard event:', {
          key: key,
          ctrl: event.ctrlKey,
          shift: event.shiftKey,
          alt: event.altKey,
          vertexCount: this.lassoTool.points.length
        });

        switch (key) {
          case 'enter':
            // Complete lasso
            event.preventDefault();
            debugLog('[ScreenLasso] ✅ Enter pressed - completing lasso');
            this.handleLassoComplete();
            break;

          case 'escape':
            // Cancel or deactivate
            event.preventDefault();
            if (this.lassoTool.points.length > 0) {
              debugLog('[ScreenLasso] 🚫 Escape pressed - cancelling current lasso');
              this.cancelLasso();
            } else {
              debugLog('[ScreenLasso] 🚫 Escape pressed - deactivating tool');
              this.deactivateLassoTool();
            }
            break;

          case 'z':
            // Undo last point (Ctrl+Z)
            if (event.ctrlKey || event.metaKey) {
              event.preventDefault();
              if (this.lassoTool.points.length > 0) {
                const removedPoint = this.lassoTool.points.pop();
                debugLog('[ScreenLasso] ↶ Undo: Removed last vertex', {
                  removed: removedPoint,
                  remainingVertices: this.lassoTool.points.length
                });
              } else {
                debugLog('[ScreenLasso] ⚠️  Undo: No vertices to remove');
              }
            }
            break;

          default:
            debugLog('[ScreenLasso] ⚠️  Unhandled key:', key);
            break;
        }
      },

      /**
       * Format polygon points for SVG (using local coordinates)
       */
      getLassoPolygonPoints() {
        return this.lassoPointsLocal
          .map(p => `${p.x},${p.y}`)
          .join(' ');
      },

      /**
       * Restore lasso from analysis result (AVA tag data)
       */
      async restoreLassoFromAnalysisResult() {
        const dialogData = this.$store.getters.GET_CROPPED_MEASUREMENT_DIALOG;
        const lassoSelections = dialogData?.analysisResult?.lassoSelections;

        if (!lassoSelections || lassoSelections.length === 0) {
          return false;
        }

        const viewer = this.getViewer();
        if (!viewer) {
          debugWarn('[ScreenLasso] ⚠️ Cannot restore lasso - viewer not ready');
          return false;
        }

        try {
          // Import multi-lasso functions
          const { addLassoToShaderFilter, setCumulativeMode, clearAllLassos: clearAllLassosGPU } = require('../utils/lassoShaderGPU');

          // Clear any existing lassos before restoring
          clearAllLassosGPU(viewer);
          this.lassoHistory = [];
          this.activeLassoIds = [];
          this.clearLassoDebugMesh();

          // Restore saved cumulative mode or default to 'intersect'
          const savedCumulativeMode = dialogData?.analysisResult?.lassoCumulativeMode || 'intersect';
          this.lassoCumulativeMode = savedCumulativeMode;
          setCumulativeMode(savedCumulativeMode);

          // Restore each lasso selection from analysis result
          for (const lasso of lassoSelections) {
            // Detect format: 'ndc' for new format, 'screen' for legacy
            const format = lasso.format || 'screen';
            // Backward compatibility: default to 'filter' if type not saved
            const lassoType = lasso.type || 'filter';

            const lassoOp = {
              id: lasso.id,
              timestamp: lasso.timestamp,
              type: lassoType,  // 'filter' | 'clean'
              polygon: lasso.polygon,
              format: format,  // Preserve format for re-saving
              cameraState: lasso.cameraState,
              filterMode: lasso.filterMode || 'inside',
              active: true,
              visible: true,
              pointsFiltered: 0,
              originalViewport: lasso.originalViewport  // Preserve if available
            };

            // Apply filter using GPU shaders with SAVED camera state (multi-lasso support)
            // Pass format to indicate if polygon is in NDC or screen coordinates
            const result = addLassoToShaderFilter(
              viewer,
              lassoOp.polygon,
              lassoOp.filterMode,
              lassoOp.id,
              lassoOp.cameraState,  // Pass saved camera state
              format,  // Format: 'ndc' or 'screen'
              lassoType  // Lasso type: 'filter' or 'clean'
            );

            if (!result.success) {
              debugError('[ScreenLasso] ❌ Failed to restore lasso filter:', result.error);
              continue;
            }

            // Track active lasso
            this.activeLassoIds.push(lassoOp.id);

            // Add to history and panel
            this.lassoHistory.push(lassoOp);
            this.lassoHistoryIndex = this.lassoHistory.length - 1;
            this.addLassoToPanel(lassoOp);

            // Render debug visualization with color based on lasso index
            this.renderLassoDebugMesh(lassoOp, this.lassoHistory.length - 1);
          }

          // Recalculate corrosion statistics for restored lasso selection
          if (this.$refs.potreeViewer && this.$refs.potreeViewer.recalculateCorrosionForLassoSelection) {
            this.$refs.potreeViewer.recalculateCorrosionForLassoSelection();
          }

          return true;
        } catch (error) {
          debugError('[ScreenLasso] ❌ Error restoring lasso from analysis:', error.message);
          return false;
        }
      },

      /**
       * Export lasso data for sharing
       */
      exportLassoData() {
        if (this.lassoHistory.length === 0) {
          debugWarn('[ScreenLasso] ⚠️ No lasso to export');
          return null;
        }

        const lassoOp = this.lassoHistory[this.lassoHistoryIndex];
        return {
          version: '1.0.0',
          timestamp: new Date().toISOString(),
          lasso: {
            id: lassoOp.id,
            polygon: lassoOp.polygon,
            cameraState: lassoOp.cameraState,
            filterMode: lassoOp.filterMode
          }
        };
      },

      /**
       * Import lasso data from JSON
       */
      async importLassoData(data) {
        if (!data || !data.lasso) {
          debugError('[ScreenLasso] ❌ Invalid import data');
          return false;
        }

        try {
          const { lasso } = data;
          const viewer = this.getViewer();

          if (!viewer) {
            debugWarn('[ScreenLasso] ⚠️ Cannot import lasso - viewer not ready');
            return false;
          }

          // Create lasso operation from imported data
          const lassoOp = {
            id: lasso.id || 'lasso_' + Date.now(),
            timestamp: data.timestamp || new Date().toISOString(),
            polygon: lasso.polygon,
            cameraState: lasso.cameraState,
            filterMode: lasso.filterMode || 'inside',
            active: true,
            visible: true,
            pointsFiltered: 0
          };

          // Apply filter
          const { applyLassoShaderFilter } = require('../utils/lassoShaderGPU');
          const result = applyLassoShaderFilter(
            viewer,
            lassoOp.polygon,
            lassoOp.filterMode,
            lassoOp.id
          );

          if (!result.success) {
            debugError('[ScreenLasso] ❌ Failed to import lasso:', result.error);
            return false;
          }

          // Add to history and panel (saved with AVA tag, not LocalStorage)
          this.lassoHistory.push(lassoOp);
          this.lassoHistoryIndex = this.lassoHistory.length - 1;
          this.addLassoToPanel(lassoOp);

          // Render debug visualization with color based on lasso index
          this.renderLassoDebugMesh(lassoOp, this.lassoHistory.length - 1);

          debugLog('[ScreenLasso] ✅ Lasso imported successfully', {
            lassoId: lassoOp.id,
            vertices: lassoOp.polygon.length
          });

          return true;
        } catch (error) {
          debugError('[ScreenLasso] ❌ Error importing lasso:', error.message);
          return false;
        }
      },

      /**
       * Render lasso as THREE.js debug visualization
       * Shows the lasso polygon projected into 3D space
       * @param {Object} lassoOp - The lasso operation to render
       * @param {number} colorIndex - Optional index for color selection (0-3)
       */
      renderLassoDebugMesh(lassoOp, colorIndex = null) {
        if (!this.lassoDebugEnabled) return;

        const viewer = this.getViewer();
        if (!viewer || !viewer.scene || !viewer.scene.scene) {
          debugWarn('[LassoDebug] Viewer not ready');
          return;
        }

        // Remove existing debug mesh for this specific lasso (if exists)
        if (this.lassoDebugMeshes[lassoOp.id]) {
          this.clearLassoDebugMesh(lassoOp.id);
        }

        // Use Potree's THREE.js to ensure compatibility with Potree's scene
        // Objects created with window.THREE (Forge) are incompatible with Potree's scene
        const THREE = window.Potree && window.Potree.THREE ? window.Potree.THREE : window.THREE;

        if (!THREE) {
          debugWarn('[LassoDebug] THREE.js not available');
          return;
        }

        debugLog('[LassoDebug] Using THREE.js from:', window.Potree.THREE ? 'Potree' : 'window');

        try {
          const cameraState = lassoOp.cameraState;
          if (!cameraState) {
            debugWarn('[LassoDebug] No camera state in lasso');
            return;
          }

          // Recreate camera from saved state
          const savedCamera = new THREE.PerspectiveCamera(
            cameraState.fov,
            cameraState.aspect,
            cameraState.near,
            cameraState.far
          );
          savedCamera.position.fromArray(cameraState.position);
          savedCamera.quaternion.fromArray(cameraState.quaternion);
          savedCamera.projectionMatrix.fromArray(cameraState.projectionMatrix);
          savedCamera.matrixWorldInverse.fromArray(cameraState.matrixWorldInverse);
          savedCamera.updateMatrixWorld();

          // Get canvas for coordinate conversion
          const canvas = viewer.renderer.domElement;
          const rect = canvas.getBoundingClientRect();

          // Convert polygon points to 3D world positions
          // Project rays from camera through each polygon vertex
          const worldPoints = [];
          const raycaster = new THREE.Raycaster();

          for (const point of lassoOp.polygon) {
            // Handle both NDC and screen coordinate formats
            let ndcX, ndcY;
            if (lassoOp.format === 'ndc') {
              // Polygon is already in NDC coordinates
              ndcX = point.x;
              ndcY = point.y;
            } else {
              // Legacy: convert client coordinates to NDC
              ndcX = ((point.x - rect.left) / rect.width) * 2 - 1;
              ndcY = -((point.y - rect.top) / rect.height) * 2 + 1;
            }

            // Create ray from saved camera through this NDC point
            raycaster.setFromCamera(new THREE.Vector2(ndcX, ndcY), savedCamera);

            // Get point at a fixed distance from camera (for visualization)
            const distance = 5; // meters from camera
            const worldPoint = raycaster.ray.origin.clone().add(
              raycaster.ray.direction.clone().multiplyScalar(distance)
            );
            worldPoints.push(worldPoint);
          }

          // Create polygon outline
          const lineGeometry = new THREE.BufferGeometry();
          const linePositions = [];

          // Add polygon edges
          for (let i = 0; i < worldPoints.length; i++) {
            const p1 = worldPoints[i];
            const p2 = worldPoints[(i + 1) % worldPoints.length];
            linePositions.push(p1.x, p1.y, p1.z);
            linePositions.push(p2.x, p2.y, p2.z);
          }

          // Add lines from camera to each vertex (frustum visualization)
          const cameraPos = new THREE.Vector3().fromArray(cameraState.position);
          for (const worldPoint of worldPoints) {
            linePositions.push(cameraPos.x, cameraPos.y, cameraPos.z);
            linePositions.push(worldPoint.x, worldPoint.y, worldPoint.z);
          }

          // Compatibility: Handle both old and new THREE.js versions
          const positionAttribute = THREE.Float32BufferAttribute
            ? new THREE.Float32BufferAttribute(linePositions, 3)
            : new THREE.BufferAttribute(new Float32Array(linePositions), 3);

          if (lineGeometry.setAttribute) {
            lineGeometry.setAttribute('position', positionAttribute);
          } else {
            lineGeometry.addAttribute('position', positionAttribute);
          }

          // Determine color based on lasso type and index
          const lassoType = lassoOp.type || 'filter';
          const colorArray = this.lassoDebugColors[lassoType] || this.lassoDebugColors.filter;
          const typeIndex = lassoType === 'clean'
            ? this.lassoHistory.filter(l => l.type === 'clean').indexOf(lassoOp)
            : this.lassoHistory.filter(l => l.type === 'filter' || !l.type).indexOf(lassoOp);
          const effectiveIndex = typeIndex >= 0 ? typeIndex : (colorIndex !== null ? colorIndex : Object.keys(this.lassoDebugMeshes).length);
          const lineColor = colorArray[effectiveIndex % colorArray.length];

          // Create material with bright color for visibility
          const lineMaterial = new THREE.LineBasicMaterial({
            color: lineColor,
            depthTest: false,  // Always visible
            transparent: true,
            opacity: 0.8
          });

          // Create line segments
          const debugLines = new THREE.LineSegments(lineGeometry, lineMaterial);
          debugLines.name = 'lasso-debug-lines';
          debugLines.renderOrder = 999;

          // Create spheres at each vertex for better visibility
          const sphereGroup = new THREE.Group();
          sphereGroup.name = 'lasso-debug-spheres';

          const sphereGeometry = new THREE.SphereGeometry(0.02, 8, 8);
          const sphereMaterial = new THREE.MeshBasicMaterial({
            color: 0xff0000,  // Red
            depthTest: false
          });

          for (const worldPoint of worldPoints) {
            const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
            sphere.position.copy(worldPoint);
            sphere.renderOrder = 1000;
            sphereGroup.add(sphere);
          }

          // Add camera position marker
          const cameraMarkerGeom = new THREE.SphereGeometry(0.03, 8, 8);
          const cameraMarkerMat = new THREE.MeshBasicMaterial({
            color: 0x0000ff,  // Blue for camera
            depthTest: false
          });
          const cameraMarker = new THREE.Mesh(cameraMarkerGeom, cameraMarkerMat);
          cameraMarker.position.copy(cameraPos);
          cameraMarker.renderOrder = 1000;
          sphereGroup.add(cameraMarker);

          // Create group to hold all debug objects
          const debugGroup = new THREE.Group();
          debugGroup.name = 'lasso-debug-group';
          debugGroup.add(debugLines);
          debugGroup.add(sphereGroup);

          // Add to scene
          viewer.scene.scene.add(debugGroup);
          this.lassoDebugMeshes[lassoOp.id] = debugGroup;

          debugLog('[LassoDebug] Rendered debug mesh:', {
            lassoId: lassoOp.id,
            colorIndex: effectiveIndex,
            vertices: worldPoints.length,
            cameraPos: cameraState.position,
            totalMeshes: Object.keys(this.lassoDebugMeshes).length
          });

        } catch (error) {
          debugError('[LassoDebug] Error rendering debug mesh:', error.message);
        }
      },

      /**
       * Clear lasso debug visualization
       * @param {string} lassoId - Optional ID of specific lasso to clear; if omitted, clears all
       */
      clearLassoDebugMesh(lassoId = null) {
        const viewer = this.getViewer();
        if (!viewer || !viewer.scene || !viewer.scene.scene) return;

        const disposeMesh = (mesh) => {
          viewer.scene.scene.remove(mesh);
          mesh.traverse((child) => {
            if (child.geometry) child.geometry.dispose();
            if (child.material) {
              if (Array.isArray(child.material)) {
                child.material.forEach(m => m.dispose());
              } else {
                child.material.dispose();
              }
            }
          });
        };

        if (lassoId !== null) {
          // Clear specific lasso mesh
          const mesh = this.lassoDebugMeshes[lassoId];
          if (mesh) {
            disposeMesh(mesh);
            delete this.lassoDebugMeshes[lassoId];
            debugLog('[LassoDebug] Cleared mesh for lasso:', lassoId);
          }
        } else {
          // Clear all lasso meshes
          Object.keys(this.lassoDebugMeshes).forEach((id) => {
            disposeMesh(this.lassoDebugMeshes[id]);
          });
          this.lassoDebugMeshes = {};
          debugLog('[LassoDebug] Cleared all debug meshes');
        }
      },

      /**
       * Toggle lasso debug visualization
       */
      toggleLassoDebug() {
        this.lassoDebugEnabled = !this.lassoDebugEnabled;
        debugLog('[LassoDebug] Debug visualization:', this.lassoDebugEnabled ? 'enabled' : 'disabled');

        if (!this.lassoDebugEnabled) {
          this.clearLassoDebugMesh();
        } else if (this.lassoHistory.length > 0) {
          // Re-render all active lassos with their respective colors
          this.lassoHistory.forEach((lasso, index) => {
            if (this.activeLassoIds.includes(lasso.id)) {
              this.renderLassoDebugMesh(lasso, index);
            }
          });
        }
      },

      /**
       * Add lasso to measurements panel
       */
      addLassoToPanel(lassoOp) {
        const isClean = lassoOp.type === 'clean';
        const measurementItem = {
          id: lassoOp.id,
          type: 'screen-lasso',
          measurement: null,  // No Potree measurement object
          lassoData: lassoOp,
          timestamp: lassoOp.timestamp,
          displayName: isClean ? `Clean ${this.cleanLassoCount}` : `Lasso ${this.filterLassoCount}`,
          icon: isClean ? 'mdi-eraser' : 'mdi-lasso',
          visible: true,
          values: [
            {
              label: 'Vertices',
              text: lassoOp.polygon.length.toString()
            },
            {
              label: 'Mode',
              text: lassoOp.filterMode.toUpperCase()
            },
            {
              label: 'Points Filtered',
              text: lassoOp.pointsFiltered ? lassoOp.pointsFiltered.toLocaleString() : '0'
            },
            {
              label: 'Created',
              text: new Date(lassoOp.timestamp).toLocaleTimeString()
            }
          ]
        };

        this.activeMeasurements.push(measurementItem);

        logEvent('Lasso Added to Panel', {
          id: lassoOp.id,
          totalMeasurements: this.activeMeasurements.length
        });
      },

      /**
       * Remove lasso from history and recalculate filters
       */
      async removeLasso(lassoId) {
        debugLog('[ScreenLasso] Removing lasso:', lassoId);

        try {
          const { removeLassoFromShaderFilter } = require('../utils/lassoShaderGPU');
          const viewer = this.getViewer();

          // Remove specific lasso from the filter set
          const result = removeLassoFromShaderFilter(viewer, lassoId);

          if (!result.success) {
            debugWarn('[ScreenLasso] Lasso not found in shader filter:', lassoId);
          } else {
            debugLog('[ScreenLasso] ✅ Lasso removed from GPU filter:', {
              lassoId,
              remainingLassos: result.remainingLassos
            });
          }

          // Remove from active lasso IDs
          const activeIndex = this.activeLassoIds.indexOf(lassoId);
          if (activeIndex !== -1) {
            this.activeLassoIds.splice(activeIndex, 1);
          }

          // Clear debug mesh for this specific lasso
          this.clearLassoDebugMesh(lassoId);

          // Remove from history
          const index = this.lassoHistory.findIndex(l => l.id === lassoId);
          if (index !== -1) {
            this.lassoHistory.splice(index, 1);
            if (this.lassoHistoryIndex >= index && this.lassoHistoryIndex > 0) {
              this.lassoHistoryIndex--;
            }
          }

          // Remove from measurements panel
          const measurementIndex = this.activeMeasurements.findIndex(m => m.id === lassoId);
          if (measurementIndex !== -1) {
            this.activeMeasurements.splice(measurementIndex, 1);
          }

          debugLog('[ScreenLasso] ✅ Lasso removed');

          // Trigger immediate re-render for visual feedback
          if (viewer && viewer.renderer) {
            viewer.renderer.render(viewer.scene.scene, viewer.scene.getActiveCamera());
          }

          // Recalculate corrosion statistics for lasso selection
          if (this.$refs.potreeViewer && this.$refs.potreeViewer.recalculateCorrosionForLassoSelection) {
            this.$refs.potreeViewer.recalculateCorrosionForLassoSelection();
          }

          logEvent('Lasso Removed', {
            lassoId,
            remainingLassos: this.lassoHistory.length,
            activeLassos: this.activeLassoIds.length
          });

        } catch (error) {
          debugError('[ScreenLasso] Error removing lasso:', error);
        }
      },

      /**
       * Clear all lassos
       */
      clearAllLassos() {
        debugLog('[ScreenLasso] Clearing all lassos');

        try {
          const { clearAllLassos: clearAllLassosGPU } = require('../utils/lassoShaderGPU');
          const viewer = this.getViewer();

          const result = clearAllLassosGPU(viewer);

          if (!result.success) {
            throw new Error(result.error || 'Failed to clear all lassos');
          }

          debugLog('[ScreenLasso] ✅ All GPU lassos cleared:', {
            clearedCount: result.clearedCount
          });

          // Clear debug mesh
          this.clearLassoDebugMesh();

          // Clear active lasso IDs
          this.activeLassoIds = [];

          // Clear history
          this.lassoHistory = [];
          this.lassoHistoryIndex = -1;

          // Remove from measurements panel
          this.activeMeasurements = this.activeMeasurements.filter(m => m.type !== 'screen-lasso');

          debugLog('[ScreenLasso] ✅ All lassos cleared');

          // Recalculate corrosion statistics (will reset to full data)
          if (this.$refs.potreeViewer && this.$refs.potreeViewer.recalculateCorrosionForLassoSelection) {
            this.$refs.potreeViewer.recalculateCorrosionForLassoSelection();
          }

          logEvent('All Lassos Cleared', {
            clearedCount: result.clearedCount
          });

        } catch (error) {
          debugError('[ScreenLasso] Error clearing lassos:', error);
        }
      },

      /**
       * Start clean lasso mode (exclusion lasso)
       * Sets mode flag and starts lasso tool
       */
      startCleanLasso() {
        debugLog('[ScreenLasso] Starting clean lasso mode');
        this.isCleanLassoMode = true;

        // Start the lasso tool
        this.setMeasurementTool('lasso');

        logEvent('Clean Lasso Mode Started', {
          existingCleanLassos: this.cleanLassoCount
        });
      },

      /**
       * Toggle a specific lasso active/inactive
       */
      toggleLassoActive(lassoId) {
        const { toggleLassoActive: toggleLassoActiveGPU } = require('../utils/lassoShaderGPU');

        // Find the lasso in history
        const lasso = this.lassoHistory.find(l => l.id === lassoId);
        if (!lasso) {
          debugWarn('[ScreenLasso] Lasso not found:', lassoId);
          return;
        }

        // Toggle the active state
        const newActiveState = !lasso.active;
        const result = toggleLassoActiveGPU(lassoId, newActiveState);

        if (result.success) {
          // Update local state
          lasso.active = newActiveState;

          // Update activeLassoIds
          if (newActiveState) {
            if (!this.activeLassoIds.includes(lassoId)) {
              this.activeLassoIds.push(lassoId);
            }
          } else {
            const index = this.activeLassoIds.indexOf(lassoId);
            if (index !== -1) {
              this.activeLassoIds.splice(index, 1);
            }
          }

          debugLog('[ScreenLasso] Lasso toggled:', {
            lassoId,
            active: newActiveState,
            activeLassos: this.activeLassoIds.length
          });

          // Recalculate corrosion statistics for toggled lasso
          if (this.$refs.potreeViewer && this.$refs.potreeViewer.recalculateCorrosionForLassoSelection) {
            this.$refs.potreeViewer.recalculateCorrosionForLassoSelection();
          }

          logEvent('Lasso Active Toggled', {
            lassoId,
            active: newActiveState,
            activeLassos: this.activeLassoIds.length
          });
        }
      },
    },
  };
</script>

<style lang="scss">
  /*
    Z-INDEX MANAGEMENT HIERARCHY:
    Layer 1000: Base layer (Potree viewer)
    Layer 1100: Toolbar layer (measurement toolbar, background selector)
    Layer 1150: Panel controls (toggle buttons)
    Layer 1200: Panel layer (measurements panel)
    Layer 1300: Dialog layer (settings dialogs)
    Layer 1400: Overlay layer (modal overlays)
  */

  /* Root container - Grid layout with sidebar and main viewer */
  .potree-measurements-app {
    width: 100%;
    height: 100%;
    position: relative;
    overflow-x: visible;
    overflow-y: hidden;
    display: grid;
    grid-template-columns: auto 1fr;
    grid-template-rows: 1fr;
  }

  .vue-potree-viewer {
    width: 100%;
    height: 100%;
    position: relative;
    overflow: hidden;
  }

  /* Ensure full viewport usage */
  .vue-potree-viewer .potree-container {
    width: 100%;
    height: 100%;
  }

  /* Allow native Potree sidebar to show */
  .vue-potree-viewer #potree_sidebar_container {
    display: block !important;
  }

  /* Ensure render area responds to native sidebar */
  .vue-potree-viewer #potree_render_area {
    width: 100%;
    height: 100%;
  }

  /* Main viewer styling - positioned for toolbar placement */
  .potree-main {
    position: relative;
    flex: 1;
    min-height: 0; /* Allow flex shrinking */
    padding: 0 !important;
    width: 100%;
    display: flex;
    flex-direction: column;
  }

  /* When used standalone (no parent with defined height), use viewport height */
  .potree-measurements-app:not([style*='height']) .potree-main {
    height: 100vh;
  }

  /* Floating background selector - Layer 1100 (Toolbar Layer) */
  .background-selector {
    position: absolute;
    top: 20px;
    right: 20px;
    z-index: 1100;
    padding: 0px;
    isolation: isolate;
    border-radius: 12px !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
    width: 150px;
  }

  .background-select {
    margin: 0;
  }

  .background-select .v-input__control {
    min-height: 40px;
  }

  .background-select .v-select__selection {
    font-size: 14px;
  }

  /* ========================================
   * Right Side Vertical Measurement Toolbar
   * Industrial Mission-Critical Design
   * Layer 1100 (Toolbar Layer)
   * ======================================== */
  .measurement-toolbar {
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1100;
    padding: 8px 6px;
    border-radius: 8px !important;
    background:  #2b2b2b !important;
    border: 2px solid rgba(3, 98, 76, 0.4) !important;
    box-shadow:
      0 4px 20px rgba(0, 0, 0, 0.4),
      inset 0 1px 0 rgba(255, 255, 255, 0.05),
      0 0 0 1px rgba(0, 0, 0, 0.3) !important;
    isolation: isolate;
    backdrop-filter: blur(8px);
  }

  /* Industrial corner accent */
  .measurement-toolbar::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg,
      transparent 0%,
      rgba(3, 98, 76, 0.6) 20%,
      rgba(3, 98, 76, 0.8) 50%,
      rgba(3, 98, 76, 0.6) 80%,
      transparent 100%);
    border-radius: 8px 8px 0 0;
  }

  .toolbar-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
  }

  /* Tools Section - Vertical Stack */
  .measurement-tools {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 4px 0;
  }

  /* Industrial Tool Buttons */
  .measurement-btn {
    width: 40px !important;
    height: 40px !important;
    min-width: 40px !important;
    margin: 0 !important;
    border-radius: 4px !important;
    background: rgba(255, 255, 255, 0.08) !important;
    border: 1px solid rgba(255, 255, 255, 0.1) !important;
    transition: all 0.15s ease !important;
  }

  .measurement-btn .v-icon {
    color: rgba(255, 255, 255, 0.85) !important;
    font-size: 20px !important;
  }

  .measurement-btn img {
    filter: brightness(0) invert(1) opacity(0.85) !important;
  }

  .measurement-btn:hover:not(:disabled) {
    background: rgba(3, 98, 76, 0.3) !important;
    border-color: rgba(3, 98, 76, 0.5) !important;
    transform: scale(1.05);
  }

  .measurement-btn:hover:not(:disabled) .v-icon {
    color: #a8c97a !important;
  }

  .measurement-btn:hover:not(:disabled) img {
    filter: brightness(0) saturate(100%) invert(75%) sepia(25%) saturate(500%) hue-rotate(50deg) !important;
  }

  .measurement-btn.v-btn--active,
  .measurement-btn:active:not(:disabled) {
    background: rgba(3, 98, 76, 0.5) !important;
    border-color: rgba(3, 98, 76, 0.8) !important;
    box-shadow:
      inset 0 2px 4px rgba(0, 0, 0, 0.3),
      0 0 8px rgba(3, 98, 76, 0.4) !important;
  }

  .measurement-btn.v-btn--active .v-icon {
    color: #c8e89a !important;
  }

  .measurement-btn:disabled {
    opacity: 0.35 !important;
    cursor: not-allowed !important;
  }

  /* Clean Lasso Button - Error State */
  .clean-lasso-btn.error--text .v-icon,
  .clean-lasso-btn[style*="color: rgb(255"] .v-icon {
    color: #ff6b6b !important;
  }

  /* Toolbar Divider */
  .toolbar-divider {
    width: 28px !important;
    margin: 6px 0 !important;
    border-color: rgba(3, 98, 76, 0.3) !important;
  }

  /* Actions Section */
  .measurement-actions {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 4px 0;
  }

  .measurement-action-btn {
    width: 40px !important;
    height: 40px !important;
    min-width: 40px !important;
    border-radius: 4px !important;
    background: rgba(255, 255, 255, 0.05) !important;
    border: 1px solid rgba(255, 255, 255, 0.08) !important;
  }

  .measurement-action-btn .v-icon {
    color: rgba(255, 255, 255, 0.7) !important;
  }

  .measurement-action-btn:hover:not(:disabled) {
    background: rgba(3, 98, 76, 0.2) !important;
  }

  .measurement-action-btn:hover:not(:disabled) .v-icon {
    color: #a8c97a !important;
  }

  /* Measurement Count Button */
  .measurement-count-btn {
    width: 40px !important;
    height: 40px !important;
    min-width: 40px !important;
    border-radius: 4px !important;
    background: rgba(3, 98, 76, 0.15) !important;
    border: 1px solid rgba(3, 98, 76, 0.3) !important;
    transition: all 0.2s ease !important;
  }

  .measurement-count-btn .v-icon {
    color: rgba(255, 255, 255, 0.85) !important;
  }

  .measurement-count-btn:hover {
    background: rgba(3, 98, 76, 0.3) !important;
    transform: scale(1.08);
    box-shadow: 0 0 12px rgba(3, 98, 76, 0.4) !important;
  }

  .measurement-count-btn .v-badge__badge {
    font-family: 'JetBrains Mono', monospace !important;
    font-size: 10px !important;
    font-weight: 700 !important;
    min-width: 16px !important;
    height: 16px !important;
    background: linear-gradient(135deg, #03624c 0%, #5a7a3a 100%) !important;
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
  }

  /* ========================================
   * Views Section - Camera Toggle & Flyout Navigation
   * ======================================== */
  .views-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 4px 0;
  }

  /* Camera Toggle Button (2D/3D) */
  .camera-toggle-btn {
    width: 40px !important;
    height: 40px !important;
    min-width: 40px !important;
    border-radius: 4px !important;
    transition: all 0.2s ease !important;
  }

  .camera-toggle-btn.perspective-mode {
    background: linear-gradient(135deg,
      rgba(33, 150, 243, 0.2) 0%,
      rgba(25, 118, 210, 0.3) 100%) !important;
    border: 1px solid rgba(33, 150, 243, 0.4) !important;
  }

  .camera-toggle-btn.perspective-mode .v-icon {
    color: #64b5f6 !important;
  }

  .camera-toggle-btn.perspective-mode:hover:not(:disabled) {
    background: linear-gradient(135deg,
      rgba(33, 150, 243, 0.35) 0%,
      rgba(25, 118, 210, 0.45) 100%) !important;
    border-color: rgba(33, 150, 243, 0.6) !important;
    box-shadow: 0 0 12px rgba(33, 150, 243, 0.4) !important;
    transform: scale(1.05);
  }

  .camera-toggle-btn.orthographic-mode {
    background: linear-gradient(135deg,
      rgba(76, 175, 80, 0.2) 0%,
      rgba(56, 142, 60, 0.3) 100%) !important;
    border: 1px solid rgba(76, 175, 80, 0.4) !important;
  }

  .camera-toggle-btn.orthographic-mode .v-icon {
    color: #81c784 !important;
  }

  .camera-toggle-btn.orthographic-mode:hover:not(:disabled) {
    background: linear-gradient(135deg,
      rgba(76, 175, 80, 0.35) 0%,
      rgba(56, 142, 60, 0.45) 100%) !important;
    border-color: rgba(76, 175, 80, 0.6) !important;
    box-shadow: 0 0 12px rgba(76, 175, 80, 0.4) !important;
    transform: scale(1.05);
  }

  .camera-toggle-btn:disabled {
    opacity: 0.35 !important;
  }

  /* Views Flyout Container */
  .views-flyout-container {
    position: relative;
    display: flex;
    align-items: center;
  }

  /* Views Trigger Button */
  .views-trigger-btn {
    width: 40px !important;
    height: 40px !important;
    min-width: 40px !important;
    border-radius: 4px !important;
    background: rgba(255, 255, 255, 0.08) !important;
    border: 1px solid rgba(255, 255, 255, 0.1) !important;
    transition: all 0.2s ease !important;
  }

  .views-trigger-btn .v-icon {
    color: rgba(255, 255, 255, 0.85) !important;
    transition: transform 0.2s ease !important;
  }

  .views-trigger-btn:hover:not(:disabled),
  .views-trigger-btn.flyout-open {
    background: rgba(3, 98, 76, 0.3) !important;
    border-color: rgba(3, 98, 76, 0.5) !important;
  }

  .views-trigger-btn:hover:not(:disabled) .v-icon,
  .views-trigger-btn.flyout-open .v-icon {
    color: #a8c97a !important;
  }

  .views-trigger-btn.flyout-open .v-icon {
    transform: rotate(-90deg);
  }

  .views-trigger-btn:disabled {
    opacity: 0.35 !important;
  }

  /* ========================================
   * Interactive 3D ViewCube
   * CAD-style navigation control
   * ======================================== */
  .viewcube-flyout {
    position: absolute;
    right: calc(100% + 12px);
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    flex-direction: column;
    background: linear-gradient(165deg,
      rgba(22, 26, 22, 0.97) 0%,
      rgba(16, 20, 16, 0.99) 100%);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 12px;
    box-shadow:
      0 16px 48px rgba(0, 0, 0, 0.6),
      0 4px 16px rgba(0, 0, 0, 0.4),
      inset 0 1px 0 rgba(255, 255, 255, 0.04);
    backdrop-filter: blur(20px);
    overflow: visible;
  }

  /* Arrow pointer */
  .viewcube-flyout::after {
    content: '';
    position: absolute;
    right: -8px;
    top: 50%;
    transform: translateY(-50%);
    border: 8px solid transparent;
    border-left-color: rgba(22, 26, 22, 0.97);
  }

  .viewcube-flyout::before {
    content: '';
    position: absolute;
    right: -10px;
    top: 50%;
    transform: translateY(-50%);
    border: 9px solid transparent;
    border-left-color: rgba(255, 255, 255, 0.12);
  }

  /* ViewCube Wrapper */
  .viewcube-wrapper {
    position: relative;
    padding: 20px;
  }

  /* 3D Scene Container */
  .viewcube-scene {
    width: 120px;
    height: 120px;
    perspective: 400px;
    perspective-origin: 50% 50%;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* The 3D Cube */
  .viewcube-scene .viewcube {
    width: 70px;
    height: 70px;
    position: relative;
    transform-style: preserve-3d;
    transform: rotateX(-25deg) rotateY(-35deg);
    transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  }

  /* Cube Faces - Base Styles */
  .cube-face {
    position: absolute;
    width: 70px;
    height: 70px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    background: linear-gradient(135deg,
      rgba(60, 75, 55, 0.85) 0%,
      rgba(45, 58, 42, 0.9) 100%);
    border: 2px solid rgba(3, 98, 76, 0.4);
    cursor: pointer;
    transition: all 0.2s ease;
    backface-visibility: visible;
    box-shadow: inset 0 0 20px rgba(0, 0, 0, 0.3);
  }

  .cube-face:hover {
    background: linear-gradient(135deg,
      rgba(80, 100, 70, 0.9) 0%,
      rgba(65, 85, 58, 0.95) 100%);
    border-color: rgba(168, 201, 122, 0.7);
  }

  .cube-face.hovered {
    background: linear-gradient(135deg,
      rgba(100, 130, 85, 0.95) 0%,
      rgba(80, 105, 70, 0.98) 100%);
    border-color: rgba(168, 201, 122, 0.9);
    box-shadow:
      inset 0 0 20px rgba(0, 0, 0, 0.2),
      0 0 20px rgba(3, 98, 76, 0.3);
  }

  .cube-face.active {
    background: linear-gradient(135deg,
      rgba(3, 98, 76, 0.9) 0%,
      rgba(100, 130, 62, 0.95) 100%);
    border-color: rgba(168, 201, 122, 1);
    box-shadow:
      inset 0 0 15px rgba(255, 255, 255, 0.1),
      0 0 25px rgba(3, 98, 76, 0.5);
  }

  /* Face Labels */
  .face-label {
    font-family: 'SF Mono', 'Consolas', monospace;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1px;
    color: rgba(255, 255, 255, 0.85);
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
  }

  .cube-face.active .face-label {
    color: #fff;
    text-shadow: 0 0 10px rgba(255, 255, 255, 0.3);
  }

  /* Keyboard Shortcut on Face */
  .face-key {
    font-family: 'SF Mono', 'Consolas', monospace;
    font-size: 8px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.4);
    padding: 2px 5px;
    background: rgba(0, 0, 0, 0.3);
    border-radius: 3px;
    border: 1px solid rgba(255, 255, 255, 0.1);
  }

  .cube-face:hover .face-key,
  .cube-face.hovered .face-key {
    color: rgba(255, 255, 255, 0.7);
    background: rgba(0, 0, 0, 0.4);
  }

  .cube-face.active .face-key {
    color: #fff;
    background: rgba(0, 0, 0, 0.4);
    border-color: rgba(255, 255, 255, 0.3);
  }

  /* Face Positions */
  .face-front  { transform: translateZ(35px); }
  .face-back   { transform: rotateY(180deg) translateZ(35px); }
  .face-left   { transform: rotateY(-90deg) translateZ(35px); }
  .face-right  { transform: rotateY(90deg) translateZ(35px); }
  .face-top    { transform: rotateX(90deg) translateZ(35px); }
  .face-bottom { transform: rotateX(-90deg) translateZ(35px); }

  /* Draggable Scene Styles */
  .viewcube-scene {
    cursor: grab;
    user-select: none;
  }

  .viewcube-scene.is-dragging {
    cursor: grabbing;
  }

  .viewcube.dragging {
    transition: none; /* Disable transition while dragging for responsive feel */
  }

  /* Drag hint indicator */
  .drag-hint {
    position: absolute;
    bottom: 4px;
    right: 4px;
    opacity: 0.6;
    transition: opacity 0.2s ease;
    pointer-events: none;
  }

  .viewcube-scene:hover .drag-hint {
    opacity: 0;
  }

  /* Compass Ring */
  .compass-ring {
    position: absolute;
    inset: 10px;
    border: 1px dashed rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    pointer-events: none;
  }

  .compass-ring span {
    position: absolute;
    font-family: 'SF Mono', monospace;
    font-size: 8px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.25);
  }

  .compass-n { top: -2px; left: 50%; transform: translateX(-50%) translateY(-100%); }
  .compass-s { bottom: -2px; left: 50%; transform: translateX(-50%) translateY(100%); }
  .compass-e { right: -2px; top: 50%; transform: translateY(-50%) translateX(100%); }
  .compass-w { left: -2px; top: 50%; transform: translateY(-50%) translateX(-100%); }

  /* Status Bar */
  .viewcube-status {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    background: rgba(0, 0, 0, 0.25);
    border-top: 1px solid rgba(255, 255, 255, 0.06);
  }

  .status-left {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .status-dot {
    width: 6px;
    height: 6px;
    background: rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    transition: all 0.3s ease;
  }

  .status-dot.active {
    background: #81C784;
    box-shadow: 0 0 8px rgba(129, 199, 132, 0.6);
    animation: pulse-glow 2s ease-in-out infinite;
  }

  .status-dot.dragging {
    background: #64B5F6;
    box-shadow: 0 0 8px rgba(100, 181, 246, 0.6);
    animation: spin-glow 0.8s linear infinite;
  }

  @keyframes pulse-glow {
    0%, 100% { box-shadow: 0 0 8px rgba(129, 199, 132, 0.6); }
    50% { box-shadow: 0 0 12px rgba(129, 199, 132, 0.9); }
  }

  @keyframes spin-glow {
    0% { box-shadow: 0 0 6px rgba(100, 181, 246, 0.4); }
    50% { box-shadow: 0 0 10px rgba(100, 181, 246, 0.8); }
    100% { box-shadow: 0 0 6px rgba(100, 181, 246, 0.4); }
  }

  .status-text {
    font-family: -apple-system, BlinkMacSystemFont, sans-serif;
    font-size: 11px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.8);
    letter-spacing: 0.3px;
  }

  .reset-hint {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .reset-hint:hover {
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(255, 255, 255, 0.2);
  }

  .reset-hint .v-icon {
    font-size: 14px !important;
    color: rgba(255, 255, 255, 0.5) !important;
  }

  .reset-hint:hover .v-icon {
    color: rgba(255, 255, 255, 0.8) !important;
  }

  /* Flyout Animations */
  .flyout-slide-enter-active {
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .flyout-slide-leave-active {
    transition: all 0.18s cubic-bezier(0.4, 0, 1, 1);
  }

  .flyout-slide-enter,
  .flyout-slide-leave-to {
    opacity: 0;
    transform: translateY(-50%) translateX(16px) scale(0.94);
  }

  /* Cube entrance animation */
  .flyout-slide-enter-active .viewcube {
    transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1) 0.1s;
  }

  .flyout-slide-enter .viewcube {
    transform: rotateX(-45deg) rotateY(-55deg) scale(0.8);
  }

  /* ========================================
   * AVA Inspect Button - Mission Critical Action
   * ======================================== */
  .ava-inspect-section {
    display: flex;
    flex-direction: column;
    padding: 4px 0;
  }

  .ava-inspect-btn {
    width: 40px !important;
    height: 40px !important;
    min-width: 40px !important;
    border-radius: 4px !important;
    background: linear-gradient(135deg,
      rgba(76, 175, 80, 0.25) 0%,
      rgba(56, 142, 60, 0.35) 100%) !important;
    border: 2px solid rgba(76, 175, 80, 0.5) !important;
    transition: all 0.2s ease !important;
    position: relative;
    overflow: hidden;
  }

  /* Pulsing glow effect for create mode */
  .ava-inspect-btn::before {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: 6px;
    background: linear-gradient(135deg,
      rgba(76, 175, 80, 0.4) 0%,
      transparent 50%,
      rgba(76, 175, 80, 0.4) 100%);
    opacity: 0;
    animation: avaInspectPulse 2s ease-in-out infinite;
  }

  @keyframes avaInspectPulse {
    0%, 100% { opacity: 0; }
    50% { opacity: 1; }
  }

  .ava-inspect-btn .v-icon {
    color: #81c784 !important;
    font-size: 20px !important;
    position: relative;
    z-index: 1;
  }

  .ava-inspect-btn:hover:not(:disabled) {
    background: linear-gradient(135deg,
      rgba(76, 175, 80, 0.4) 0%,
      rgba(56, 142, 60, 0.5) 100%) !important;
    border-color: rgba(76, 175, 80, 0.8) !important;
    transform: scale(1.08);
    box-shadow:
      0 0 16px rgba(76, 175, 80, 0.5),
      inset 0 0 8px rgba(76, 175, 80, 0.2) !important;
  }

  .ava-inspect-btn:hover:not(:disabled) .v-icon {
    color: #a5d6a7 !important;
  }

  .ava-inspect-btn:active:not(:disabled) {
    transform: scale(0.95);
    box-shadow:
      inset 0 2px 6px rgba(0, 0, 0, 0.3),
      0 0 12px rgba(76, 175, 80, 0.4) !important;
  }

  .ava-inspect-btn:disabled {
    opacity: 0.35 !important;
    cursor: not-allowed !important;
    background: rgba(255, 255, 255, 0.05) !important;
    border-color: rgba(255, 255, 255, 0.1) !important;
  }

  .ava-inspect-btn:disabled::before {
    animation: none;
  }

  .ava-inspect-btn:disabled .v-icon {
    color: rgba(255, 255, 255, 0.4) !important;
  }

  /* Update mode - Orange/Amber variant */
  .ava-inspect-btn.update-mode {
    background: linear-gradient(135deg,
      rgba(255, 152, 0, 0.25) 0%,
      rgba(245, 124, 0, 0.35) 100%) !important;
    border-color: rgba(255, 152, 0, 0.5) !important;
  }

  .ava-inspect-btn.update-mode::before {
    background: linear-gradient(135deg,
      rgba(255, 152, 0, 0.4) 0%,
      transparent 50%,
      rgba(255, 152, 0, 0.4) 100%);
  }

  .ava-inspect-btn.update-mode .v-icon {
    color: #ffb74d !important;
  }

  .ava-inspect-btn.update-mode:hover:not(:disabled) {
    background: linear-gradient(135deg,
      rgba(255, 152, 0, 0.4) 0%,
      rgba(245, 124, 0, 0.5) 100%) !important;
    border-color: rgba(255, 152, 0, 0.8) !important;
    box-shadow:
      0 0 16px rgba(255, 152, 0, 0.5),
      inset 0 0 8px rgba(255, 152, 0, 0.2) !important;
  }

  .ava-inspect-btn.update-mode:hover:not(:disabled) .v-icon {
    color: #ffe0b2 !important;
  }

  /* Enhanced Floating Measurements Panel - Layer 1200 (Panel Layer) */
  .measurements-panel {
    position: absolute;
    left: 0; /* Positioned via transform only */
    top: 0; /* Positioned via transform only */
    width: 320px;
    max-height: calc(100vh - 120px);
    z-index: var(--z-viewer-panels); /* 1200 */
    background: white;
    border-radius: 8px !important;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15) !important;
    overflow: hidden;
    isolation: isolate; /* Create new stacking context */
    transition: box-shadow 0.2s ease, border 0.2s ease;
    will-change: transform;
    touch-action: none; /* Prevent touch scrolling during drag */
  }

  /* Visual stacking indicator - Enhance on hover to show interactivity */
  .measurements-panel:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25) !important;
    border-color: rgba(3, 98, 76, 0.3); /* Primary color hint */
  }

  /* Dragging state with focus ring */
  .measurements-panel.dragging {
    cursor: grabbing !important;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.25) !important;
    user-select: none;
    transition: none; /* Disable transitions during drag for smoother movement */
    border-color: rgba(3, 98, 76, 0.6) !important; /* Active focus ring */
    outline: 2px solid rgba(3, 98, 76, 0.3);
    outline-offset: 2px;
  }

  .viewer-panel-header {
    display: flex;
    align-items: center;
    padding: 8px 16px;
    background: var(--v-primary-base);
    color: white;
    min-height: 36px;
    position: relative;
    user-select: none; /* Prevent text selection */
  }

  /* Drag handle functionality */
  .viewer-panel-header.drag-handle {
    cursor: grab;
    transition: background-color 0.2s ease;
  }

  .viewer-panel-header.drag-handle:hover {
    background: var(--v-primary-darken1);
  }

  .viewer-panel-header.drag-handle:active {
    cursor: grabbing;
  }

  .drag-icon {
    opacity: 0.8;
    transition: opacity 0.2s ease;
    pointer-events: none; /* Prevent icon from interfering with drag */
  }

  .viewer-panel-header:hover .drag-icon {
    opacity: 1;
  }

  .panel-title {
    font-size: 16px;
    font-weight: 600;
  }

  .panel-close-btn {
    color: white !important;
    transition: transform 0.2s ease;
  }

  .panel-close-btn:hover {
    transform: scale(1.1);
  }

  .panel-content {
    overflow-y: auto;
    padding: 8px;
    transition: padding 0.2s ease;
  }

  .measurement-item {
    padding: 8px 12px;
    border-radius: 6px;
    margin-bottom: 2px;
    transition: all 0.2s ease;
    border: 1px solid transparent;
  }

  .measurement-item:last-child {
    margin-bottom: 0;
  }

  /* Compact styling - now default */
  .measurement-item.compact {
    padding: 6px 12px;
    margin-bottom: 2px;
  }

  /* Cylinder selection styles */
  .measurement-item.cylinder-selectable {
    cursor: pointer;
    transition: background-color 0.2s ease, border-left 0.2s ease;
  }

  .measurement-item.cylinder-selectable:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }

  .measurement-item.cylinder-selected {
    background-color: rgba(3, 98, 76, 0.1) !important;
    border-left: 3px solid #03624c;
    padding-left: 9px; /* Adjust for border */
  }

  .theme--dark .measurement-item.cylinder-selectable:hover {
    background-color: rgba(255, 255, 255, 0.05);
  }

  .theme--dark .measurement-item.cylinder-selected {
    background-color: rgba(3, 98, 76, 0.15) !important;
  }

  .measurement-header {
    display: flex;
    align-items: center;
    margin-bottom: 0;
    min-height: 32px;
  }

  .measurement-info {
    display: flex;
    flex-direction: column;
    flex: 1;
    margin-left: 8px;
    min-width: 0;
  }

  .measurement-name {
    font-weight: 500;
    font-size: 13px;
    line-height: 1.2;
    color: rgba(0, 0, 0, 0.87);
  }

  .measurement-value-compact {
    font-size: 11px;
    font-weight: 400;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    color: rgba(0, 0, 0, 0.6);
    line-height: 1.3;
    margin-top: 1px;
    word-break: break-word;
    overflow-wrap: break-word;
  }

  .measurement-icon {
    margin-right: 12px;
    color: var(--v-primary-base);
  }

  .measurement-icon.type-distance {
    color: #2196f3;
  }

  .measurement-icon.type-area {
    color: #4caf50;
  }

  .measurement-icon.type-volume {
    color: #ff9800;
  }

  .measurement-icon.type-angle {
    color: #9c27b0;
  }

  .measurement-icon.type-point {
    color: #f44336;
  }

  .measurement-icon.type-fitted-cylinder {
    color: #00bcd4;
  }

  .measurement-name {
    font-weight: 500;
    font-size: 14px;
  }

  .measurement-delete-btn {
    color: rgba(0, 0, 0, 0.54) !important;
  }

  .measurement-delete-btn:hover {
    color: #f44336 !important;
    background-color: rgba(244, 67, 54, 0.1);
  }

  /* Lasso mode toggle button */
  .measurement-toggle-btn {
    margin-left: 4px;
    margin-right: 4px;
  }

  .measurement-toggle-btn .v-icon {
    transition: color 0.2s ease, transform 0.2s ease;
  }

  .measurement-toggle-btn:hover .v-icon {
    transform: scale(1.1);
  }

  .measurement-values {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .measurement-value {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 13px;
    padding: 2px 0;
  }

  .value-label {
    color: rgba(0, 0, 0, 0.6);
    font-weight: 500;
    min-width: 80px;
  }

  .value-text {
    font-weight: 400;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    color: rgba(0, 0, 0, 0.87);
  }

  .no-measurements {
    text-align: center;
    padding: 32px 16px;
    color: rgba(0, 0, 0, 0.6);
  }

  .no-measurements .v-icon {
    font-size: 48px;
    opacity: 0.5;
  }

  .panel-actions {
    padding: 12px 16px;
    background: rgba(0, 0, 0, 0.02);
  }

  /* Measurements Toggle Button - Layer 1150 (Panel Controls) */
  /* Note: Currently not used in template, but kept for future use */
  .measurements-toggle-btn {
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: var(--z-viewer-toolbars); /* 1100 - Consistent with other toolbars */
    transition: transform 0.3s ease;
    isolation: isolate;
  }

  /* Shift left when measurements panel is open */
  .measurements-toggle-btn.panel-open {
    transform: translateX(-360px);
  }


  /* Responsive adjustments for measurements panel */
  @media (max-width: 1200px) {
    .measurements-panel {
      right: 20px;
      width: 300px;
    }
  }

  /* Cylinder Mode Control Panel Floating - Layer 1450 */
  .cylinder-panel-floating {
    position: fixed;
    left: 0; /* Positioned via transform only */
    top: 0; /* Positioned via transform only */
    z-index: var(--z-viewer-instructions); /* 1450 */
    max-width: 340px;
    animation: slideInLeft 0.3s ease-out;
    will-change: transform;
    transition: box-shadow 0.2s ease, filter 0.2s ease;
  }

  /* Visual stacking indicator - Subtle glow on hover */
  .cylinder-panel-floating:not(.dragging):hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
    filter: brightness(1.02);
  }

  /* Active focus ring when dragging */
  .cylinder-panel-floating.dragging {
    cursor: grabbing;
    user-select: none;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.3) !important;
    outline: 2px solid rgba(3, 98, 76, 0.4);
    outline-offset: 2px;
  }

  @keyframes slideInLeft {
    from {
      transform: translateX(-100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  /* Disabled toolbar button styling */
  .measurement-btn.disabled-in-cylinder-mode {
    opacity: 0.3 !important;
    cursor: not-allowed !important;
  }

  /* Responsive adjustments for cylinder panel */
  @media (max-width: 768px) {
    .cylinder-panel-floating {
      top: 60px;
      left: 10px;
      right: 10px;
      max-width: none;
    }
  }

  /* Corrosion Analysis Floating Panel - Layer 1310 */
  .corrosion-panel-floating {
    position: fixed;
    left: 0; /* Positioned via transform only */
    top: 0; /* Positioned via transform only */
    z-index: var(--z-viewer-floating); /* 1310 */
    max-width: 450px;
    animation: slideInRight 0.3s ease-out;
    will-change: transform;
    transition: box-shadow 0.2s ease, filter 0.2s ease;
  }

  /* Visual stacking indicator - Subtle glow on hover */
  .corrosion-panel-floating:not(.dragging):hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
    filter: brightness(1.02);
  }

  /* Active focus ring when dragging */
  .corrosion-panel-floating.dragging {
    cursor: grabbing;
    user-select: none;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.3) !important;
    outline: 2px solid rgba(3, 98, 76, 0.4);
    outline-offset: 2px;
  }

  @keyframes slideInRight {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  /* Responsive adjustments for corrosion panel */
  @media (max-width: 960px) {
    .corrosion-panel-floating {
      top: 60px;
      right: 10px;
      left: 10px;
      max-width: none;
    }
  }

  /* ========================================
   * Lasso Selection Overlay Styles
   * ======================================== */

  .lasso-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1500;  /* Above viewer (1000) and toolbar (1100) */
    pointer-events: auto;
    background-color: transparent;
  }

  .lasso-overlay.locked {
    cursor: crosshair;
  }

  /* Polygon being drawn */
  .lasso-polygon {
    fill: rgba(0, 150, 255, 0.15);
    stroke: #0096ff;
    stroke-width: 2;
    stroke-dasharray: 5, 5;
    animation: lasso-dash 1s linear infinite;
    pointer-events: none;
  }

  .lasso-polygon.inside {
    fill: rgba(0, 255, 0, 0.15);
    stroke: #00ff00;
  }

  .lasso-polygon.outside {
    fill: rgba(255, 0, 0, 0.15);
    stroke: #ff0000;
  }

  /* Vertex markers */
  .lasso-vertex {
    fill: #ffffff;
    stroke: #0096ff;
    stroke-width: 2;
    cursor: pointer;
    pointer-events: auto;
    transition: all 0.2s ease;
  }

  .lasso-vertex:hover {
    fill: #0096ff;
    r: 7;
    stroke-width: 3;
  }

  /* Preview line to cursor */
  .lasso-preview-line {
    stroke: #0096ff;
    stroke-width: 1;
    stroke-dasharray: 3, 3;
    opacity: 0.5;
    pointer-events: none;
  }

  /* Instruction text */
  .lasso-instruction {
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    font-size: 16px;
    font-weight: 500;
    fill: #ffffff;
    stroke: #000000;
    stroke-width: 4;
    paint-order: stroke;
    pointer-events: none;
    user-select: none;
  }

  /* Mode indicator */
  .lasso-mode-indicator {
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    font-size: 14px;
    font-weight: 600;
    fill: #ffeb3b;
    stroke: #000000;
    stroke-width: 3;
    paint-order: stroke;
    pointer-events: none;
    user-select: none;
  }

  .lasso-mode-hint {
    font-size: 12px;
    font-weight: 400;
    fill: #ffffff;
    opacity: 0.8;
  }

  /* Dash animation for polygon */
  @keyframes lasso-dash {
    to {
      stroke-dashoffset: -10;
    }
  }

  /* Responsive adjustments for lasso */
  @media (max-width: 960px) {
    .lasso-instruction {
      font-size: 14px;
    }

    .lasso-mode-indicator {
      font-size: 12px;
    }

    .lasso-mode-hint {
      font-size: 10px;
    }
  }
</style>
